import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getAppointmentOrders, getReportById, reviewReport, updateReportContent, retryReportGeneration, getChecksByVisitId, getVisitById, getPatientClinicalProfile, getConversationMessagesByVisitId, getPendingChecksByVisitId, reviewPendingCheck, createPendingCheck, deletePendingCheck } from '../api/reportApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save } from 'lucide-react';
import { useAuth } from '../utils/auth';
import ReportConversationHistory from '../components/ReportConversationHistory';
import MarkdownRichEditor from '../components/MarkdownRichEditor';
import HealthHouseCheckList from '../components/HealthHouseCheckList';
import MedicalOrderDraftEditor from '../components/MedicalOrderDraftEditor';
import PrescriptionDraftEditor from '../components/PrescriptionDraftEditor';
import { getAppointmentOrderTypeLabel } from '../utils/appointmentOrderOptions';
import { prepareMedicalOrderDrafts } from '../utils/medicalOrderDrafts';
import {
  createEmptyPrescriptionDraft,
  preparePrescriptionDrafts
} from '../utils/prescriptionDrafts';
import {
  formatDateTime,
  formatReportDateTime,
  getReportLocaleDirection,
  normalizeUiLocale,
  resolveReportLocale,
} from '../utils/locale';

const REPORT_GENERATION_POLL_MS = 3000;
const SHOW_LEGACY_CHECK_SECTIONS = false;

const PROFILE_CATEGORIES = [
  'allergy',
  'medication',
  'condition',
  'surgery',
  'family_history',
  'obstetric_history',
  'social_history',
  'other_history'
].map((value) => ({ value }));

const profileStatusLabelKeys = {
  patient_reported: 'reports.profilePatientReported',
  doctor_confirmed: 'reports.profileDoctorConfirmed'
};

const getProfileFactKey = (fact) => fact.id || fact.temp_id;

const normalizeProfileDraftFact = (fact = {}) => ({
  ...fact,
  temp_id: fact.temp_id || (fact.id ? null : `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
  category: fact.category || 'other_history',
  label: fact.label || '',
  value: fact.value || '',
  attributes: fact.attributes || {},
  status: fact.status || 'patient_reported'
});

const sameProfileIdentity = (left, right) => (
  String(left?.category || '').trim() === String(right?.category || '').trim() &&
  String(left?.label || '').trim().toLowerCase() === String(right?.label || '').trim().toLowerCase()
);

const OPS_MD_TOOLS_KEY = 'ops.mdTools';
const OPS_MD_TOOLS_ENABLED_VALUE = 'enabled';
const OPS_MD_TOOLS_CONSOLE_MESSAGE = [
  '[ReportReview][Ops] MD 导出按钮默认隐藏 / Markdown export is hidden by default.',
  "显示 / Show: localStorage.setItem('ops.mdTools', 'enabled'); location.reload();",
  "隐藏 / Hide: localStorage.removeItem('ops.mdTools'); location.reload();"
].join('\n');

const isOpsMarkdownToolsEnabled = () => {
  try {
    return window.localStorage.getItem(OPS_MD_TOOLS_KEY) === OPS_MD_TOOLS_ENABLED_VALUE;
  } catch (error) {
    return false;
  }
};


/* 
// 添加Markdown样式CSS（保留以便后续可能使用）
const markdownStyles = `
  .markdown-content {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    line-height: 1.6;
    color: #333;
    text-align: start;
  }
  
  .markdown-content h1 {
    color: #2c3e50;
    font-size: 1.8em;
    margin-top: 1.5em;
    margin-bottom: 0.8em;
    padding-bottom: 0.3em;
    border-bottom: 1px solid #eaecef;
    text-align: start;
    font-weight: 700;
  }
  
  .markdown-content h2 {
    color: #3498db;
    font-size: 1.5em;
    margin-top: 1.2em;
    margin-bottom: 0.6em;
    text-align: start;
    font-weight: 600;
  }
  
  .markdown-content h3 {
    color: #2980b9;
    font-size: 1.3em;
    margin-top: 1em;
    margin-bottom: 0.5em;
    text-align: start;
  }
  
  .markdown-content p {
    margin-top: 0.5em;
    margin-bottom: 1em;
    text-align: start;
  }
  
  .markdown-content ul, .markdown-content ol {
    margin-top: 0.5em;
    margin-bottom: 1em;
    padding-inline-start: 2em;
    text-align: start;
  }
  
  .markdown-content li {
    margin-bottom: 0.3em;
    text-align: start;
  }
  
  .markdown-content img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 1.5em 0;
    border-radius: 4px;
  }
  
  .markdown-content blockquote {
    margin: 1em 0;
    padding: 0.5em 1em;
    border-inline-start: 4px solid #3498db;
    background-color: #f8f9fa;
    color: #666;
  }
  
  .markdown-content code {
    font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
    background-color: #f6f8fa;
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-size: 0.9em;
  }
  
  .markdown-content pre {
    background-color: #f6f8fa;
    padding: 1em;
    overflow-x: auto;
    border-radius: 4px;
    margin: 1em 0;
  }
  
  .markdown-content pre code {
    background-color: transparent;
    padding: 0;
    font-size: 0.9em;
    color: #333;
  }
  
  .markdown-content table {
    border-collapse: collapse;
    width: 100%;
    margin: 1em 0;
    overflow-x: auto;
    display: block;
  }
  
  .markdown-content table th {
    background-color: #f8f9fa;
    border: 1px solid #dfe2e5;
    padding: 0.5em 1em;
    font-weight: 600;
  }
  
  .markdown-content table td {
    border: 1px solid #dfe2e5;
    padding: 0.5em 1em;
  }
  
  .markdown-content hr {
    height: 0.2em;
    padding: 0;
    margin: 1.5em 0;
    background-color: #e1e4e8;
    border: 0;
  }
  
  .markdown-content a {
    color: #0366d6;
    text-decoration: none;
  }
  
  .markdown-content a:hover {
    text-decoration: underline;
  }
  
  // 检查项列表样式
  .checks-container {
    margin-bottom: 20px;
    border-radius: 8px;
    background-color: #fff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    padding: 15px;
  }
  
  .checks-list-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
  }
  
  .checks-list-table th {
    background-color: #f0f7fb;
    padding: 10px;
    text-align: start;
    font-weight: bold;
    color: #2c3e50;
    border-bottom: 2px solid #ddd;
  }
  
  .checks-list-table td {
    padding: 12px 10px;
    border-bottom: 1px solid #eee;
    vertical-align: middle;
  }
  
  .check-row:hover {
    background-color: #f8f9fa;
  }
  
  .check-type {
    font-weight: bold;
    color: #2c3e50;
  }
  
  .check-date {
    color: #7f8c8d;
    font-size: 13px;
    white-space: nowrap;
  }
  
  .check-toggle-btn {
    background-color: #f0f7fb;
    border: 1px solid #d6eaf8;
    color: #3498db;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
  }
  
  .check-toggle-btn:hover {
    background-color: #d6eaf8;
  }
  
  .check-details {
    display: none;
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 4px;
    margin-top: 10px;
  }
  
  .check-metadata {
    margin-bottom: 15px;
  }
  
  .check-metadata table {
    width: 100%;
    border-collapse: collapse;
  }
  
  .check-metadata th {
    text-align: start;
    padding: 8px;
    background-color: #eef7fa;
    border: 1px solid #dde6e9;
    width: 30%;
    font-weight: 600;
  }
  
  .check-metadata td {
    padding: 8px;
    border: 1px solid #dde6e9;
  }
`;
*/



const ReportReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const uiLocale = normalizeUiLocale(i18n.resolvedLanguage || i18n.language);
  const { currentUser } = useAuth();

  const [report, setReport] = useState(null);
  const [visit, setVisit] = useState(null);
  const reportLocale = resolveReportLocale(report, visit);
  const reportDirection = getReportLocaleDirection(reportLocale);
  const [reportContent, setReportContent] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [viewMode, setViewMode] = useState('preview'); // 默认为预览模式
  const [contentBeforeEdit, setContentBeforeEdit] = useState('');
  const [savingContent, setSavingContent] = useState(false);
  const [checks, setChecks] = useState([]);
  const [checksLoading, setChecksLoading] = useState(true);
  const [checksError, setChecksError] = useState('');
  const [checksReady, setChecksReady] = useState(false); // 检查项是否已准备好

  // 待检查项目相关状态
  const [pendingChecks, setPendingChecks] = useState([]);
  const [pendingChecksLoading, setPendingChecksLoading] = useState(false);
  const [pendingChecksError, setPendingChecksError] = useState('');
  const [appointmentOrders, setAppointmentOrders] = useState([]);
  const [examinationDrafts, setExaminationDrafts] = useState([{ check_type: '', description: '' }]);
  const [prescriptionDrafts, setPrescriptionDrafts] = useState([createEmptyPrescriptionDraft()]);
  const [invalidPrescriptionRows, setInvalidPrescriptionRows] = useState([]);
  const [appointmentDrafts, setAppointmentDrafts] = useState([{ check_type: '', description: '' }]);
  const [patientProfile, setPatientProfile] = useState(null);
  const [profileDraftFacts, setProfileDraftFacts] = useState([]);
  const [profileRemovedFactIds, setProfileRemovedFactIds] = useState([]);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const prescriptionEditorRef = useRef(null);
  
  // 审核待检查项目相关状态
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  
  // 创建待检查项目相关状态
  const [showCreatePendingCheck, setShowCreatePendingCheck] = useState(false);
  const [createPendingCheckData, setCreatePendingCheckData] = useState({
    check_type: '',
    description: ''
  });
  const [creatingPendingCheck, setCreatingPendingCheck] = useState(false);

  // 对话相关状态
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [conversationsError, setConversationsError] = useState('');
  const [showConversations, setShowConversations] = useState(false);

  // 新增状态用于段落编辑
  const [editingSection, setEditingSection] = useState(null); // 当前正在编辑的段落索引
  const [sectionContent, setSectionContent] = useState(''); // 当前编辑段落的内容
  const [parsedSections, setParsedSections] = useState([]); // 解析后的段落数组
  const [showOpsMarkdownTools] = useState(isOpsMarkdownToolsEnabled);

  // 解析markdown内容为段落
  const parseMarkdownSections = useCallback((content) => {
    if (!content || typeof content !== 'string') {
      return [];
    }

    const lines = content.split('\n');
    const sections = [];
    let currentSection = null;
    let currentContent = [];
    let currentTitleLine = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // 检查是否是h2标题
      if (line.startsWith('## ')) {
        // 保存上一个段落
        if (currentSection) {
          sections.push({
            title: currentSection,
            titleLine: currentTitleLine,
            content: currentContent.join('\n').trim(),
            startIndex: sections.length
          });
        }

        // 开始新段落
        currentSection = line.substring(3).trim(); // 去掉 "## "
        currentTitleLine = line; // 保存完整的标题行
        currentContent = []; // 不包含标题行，只包含内容
      } else {
        // 添加到当前段落内容
        currentContent.push(line);
      }
    }

    // 保存最后一个段落
    if (currentSection) {
      sections.push({
        title: currentSection,
        titleLine: currentTitleLine,
        content: currentContent.join('\n').trim(),
        startIndex: sections.length
      });
    }

    // 如果没有h2标题，将整个内容作为一个段落
    if (sections.length === 0 && content.trim()) {
      sections.push({
        title: t('reports.entireReport'),
        titleLine: '',
        content: content.trim(),
        startIndex: 0
      });
    }

    return sections;
  }, [t]);

  // 重新组合段落内容
  const reassembleSections = (sections) => {
    return sections.map(section => {
      if (section.titleLine) {
        // 如果有标题行，将标题行和内容组合
        return section.titleLine + '\n' + section.content;
      } else {
        // 如果没有标题行（整个报告的情况），只返回内容
        return section.content;
      }
    }).join('\n\n');
  };

  // 开始编辑段落
  const startEditingSection = (sectionIndex) => {
    if (report?.status !== 'pending') {
      setError(t('reports.reportNotEditable'));
      return;
    }

    const section = parsedSections[sectionIndex];
    if (section) {
      setEditingSection(sectionIndex);
      setSectionContent(section.content);
    }
  };

  // 保存段落编辑
  const saveSectionEdit = async () => {
    if (editingSection === null) return;
    if (report?.status !== 'pending') {
      setError(t('reports.reportNotEditable'));
      return;
    }

    try {
      // 更新段落内容
      const updatedSections = [...parsedSections];
      updatedSections[editingSection] = {
        ...updatedSections[editingSection],
        content: sectionContent
      };

      // 重新组合完整内容
      const newContent = reassembleSections(updatedSections);

      // 更新状态
      setReportContent(newContent);
      setParsedSections(updatedSections);

      // 自动保存到服务器
      await updateReportContent(id, newContent);

      // 关闭编辑模式
      setEditingSection(null);
      setSectionContent('');

      setSuccessMessage(t('reports.sectionUpdateSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('保存段落编辑失败:', error.response?.data || error.message || error);
      setError(t('reports.sectionUpdateError'));
    }
  };

  // 取消段落编辑
  const cancelSectionEdit = () => {
    setEditingSection(null);
    setSectionContent('');
  };

  const getProfileCategoryLabel = (category) => {
    const match = PROFILE_CATEGORIES.find((item) => item.value === category);
    if (!match) {
      return category || t('reports.profileUncategorized');
    }
    return t(`patients.profileCategories.${match.value}`);
  };

  const getProfileStatusLabel = (status) => {
    const labelKey = profileStatusLabelKeys[status];
    if (!labelKey) {
      return status || t('reports.profileUnconfirmed');
    }
    return t(labelKey);
  };

  const resetProfileDraft = (profileData) => {
    const activeFacts = (profileData?.facts || [])
      .filter((fact) => fact.status !== 'inactive')
      .map((fact) => normalizeProfileDraftFact(fact));
    setPatientProfile(profileData || null);
    setProfileDraftFacts(activeFacts);
    setProfileRemovedFactIds([]);
  };

  const fetchPatientProfile = async (patientId) => {
    if (!patientId) {
      resetProfileDraft(null);
      return null;
    }

    try {
      setProfileLoading(true);
      setProfileError('');
      const profileData = await getPatientClinicalProfile(patientId);
      resetProfileDraft(profileData);
      return profileData;
    } catch (profileRequestError) {
      console.error(`获取患者ID${patientId}长期资料失败:`, profileRequestError);
      setProfileError(
        t('reports.profileLoadError') +
        ': ' +
        (profileRequestError.response?.data?.error || profileRequestError.message || t('common.unknownError'))
      );
      resetProfileDraft(null);
      return null;
    } finally {
      setProfileLoading(false);
    }
  };

  const updateProfileDraftFact = (factKey, field, value) => {
    setProfileDraftFacts((current) => current.map((fact) => (
      getProfileFactKey(fact) === factKey
        ? { ...fact, [field]: value }
        : fact
    )));
  };

  const addProfileDraftFact = () => {
    setProfileDraftFacts((current) => [
      ...current,
      normalizeProfileDraftFact({
        category: 'other_history',
        label: '',
        value: '',
        status: 'doctor_confirmed'
      })
    ]);
  };

  const removeProfileDraftFact = (fact) => {
    const factKey = getProfileFactKey(fact);
    setProfileDraftFacts((current) => current.filter((item) => getProfileFactKey(item) !== factKey));
    if (fact.id) {
      setProfileRemovedFactIds((current) => (
        current.includes(fact.id) ? current : [...current, fact.id]
      ));
    }
  };

  const buildProfileReviewOperations = () => {
    if (!(report?.report_type === 'final')) {
      return [];
    }

    const originalFactsByID = new Map((patientProfile?.facts || []).map((fact) => [fact.id, fact]));
    const operations = [];

    profileRemovedFactIds.forEach((factID) => {
      operations.push({
        action: 'inactivate',
        fact_id: factID
      });
    });

    profileDraftFacts.forEach((fact) => {
      const label = String(fact.label || '').trim();
      const value = String(fact.value || '').trim();
      if (!label || !value) {
        return;
      }

      const originalFact = fact.id ? originalFactsByID.get(fact.id) : null;
      const payload = {
        category: fact.category,
        label,
        value,
        attributes: fact.attributes || {}
      };

      if (originalFact && !profileRemovedFactIds.includes(fact.id) && !sameProfileIdentity(originalFact, fact)) {
        operations.push({
          action: 'inactivate',
          fact_id: fact.id
        });
      }

      operations.push({
        action: 'upsert',
        ...payload
      });
    });

    return operations;
  };

  // 检查是否还有待审核的检查项目
  const hasPendingChecks = () => {
    return pendingChecks && pendingChecks.some(check => check.status === 'pending');
  };

  const shouldBlockReviewForPendingChecks = () => {
    return false;
  };

  const shouldShowPendingChecksSection = () => {
    return report && report.report_type !== 'proposal';
  };

  const getApproveButtonText = () => {
    if (submitting) {
      return t('reports.submitting');
    }
    if (report?.report_type === 'proposal') {
      return t('reports.approveDraftAndContinue');
    }
    if (report?.workflow === 'single_review' || report?.report_flow_version === 2) {
      return t('reports.approveCompleteRecord');
    }
    if (report?.report_type === 'final') {
      return t('reports.approveFinalAndComplete');
    }
    return t('reports.approve');
  };

  // 检查是否可以添加新的待检查项目
  const canAddPendingCheck = () => {
    return report && 
           report.report_type === 'proposal' && 
           report.status === 'pending';
  };

  // 判断待检查项是否是用户创建的（通过review_note字段判断）
  const isUserCreatedCheck = (pendingCheck) => {
    // 如果review_note有值且不为空字符串，说明是用户创建的
    return pendingCheck.review_note && pendingCheck.review_note.trim() !== '';
  };

  // 当reportContent变化时，重新解析段落
  useEffect(() => {
    const sections = parseMarkdownSections(reportContent);
    setParsedSections(sections);
  }, [reportContent, parseMarkdownSections]);

  useEffect(() => {
    console.info(OPS_MD_TOOLS_CONSOLE_MESSAGE);
  }, []);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const data = await getReportById(id);
        setReport(data);
        if (data.status === 'generation_failed') {
          setError(data.generation_error || t('reports.generationFailedRetry'));
        }

        console.log('report data', data);

        // 如果病历内容是字符串，直接使用
        if (typeof data.content === 'string' && data.content.trim() !== '') {
          setReportContent(data.content);
        } else if (data.content && typeof data.content === 'object' && Object.keys(data.content).length > 0) {
          // 如果病历内容是非空对象，转换为JSON字符串
          setReportContent(JSON.stringify(data.content, null, 2));
        } else {
          // 如果病历内容为空，设置为空字符串
          setReportContent('');
        }

        // 获取相关的检查项和对话消息
        if (data.visit_id) {
          await fetchChecks(data.visit_id);
          fetchConversations(data.visit_id);
          fetchAppointmentOrders(data.visit_id);
          
          // 获取待检查项目 - 需要 await 以确保数据加载完成
          await fetchPendingChecks(data.visit_id);

          // 报告创建和首次生成由后端工作流负责；审核页只读取状态。
          try {
            const visitData = await getVisitById(data.visit_id);
            setVisit(visitData);
            fetchPatientProfile(visitData.patient_id);
          } catch (visitError) {
            console.error(`获取就诊信息失败:`, visitError);
            // 即使 visit 信息暂时不可用，报告本身仍可展示。
          }
        } else {
          // 如果没有visit_id，直接设为ready
          setChecksReady(true);
          resetProfileDraft(null);
        }

        setLoading(false);
      } catch (error) {
        console.error('获取报告详情失败:', error.response?.data || error.message || error);
        setError(t('reports.fetchReportError'));
        setChecksReady(true); // 即使出错也设为ready，避免按钮永远不显示
        setLoading(false);
      }
    };

    fetchReport();
    // Keep this loader route-driven; helper deps include mutable report/check state
    // and can retrigger automatic report generation after the initial load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, t]);

  useEffect(() => {
    if (report?.status !== 'generating') {
      if (generating) {
        setGenerating(false);
      }
      return undefined;
    }

    setGenerating(true);
    const pollReportGeneration = async () => {
      try {
        const latestReport = await getReportById(id);
        setReport(latestReport);
        if (typeof latestReport.content === 'string') {
          setReportContent(latestReport.content);
        } else if (latestReport.content && typeof latestReport.content === 'object' && Object.keys(latestReport.content).length > 0) {
          setReportContent(JSON.stringify(latestReport.content, null, 2));
        }

        if (latestReport.status === 'pending') {
          setGenerating(false);
          setSuccessMessage(t('reports.generationSuccess'));
        } else if (latestReport.status === 'generation_failed') {
          setGenerating(false);
          setError(latestReport.generation_error || t('reports.generationFailedRetry'));
        }
      } catch (error) {
        console.error('轮询报告生成状态失败:', error.response?.data || error.message || error);
      }
    };

    const intervalId = setInterval(pollReportGeneration, REPORT_GENERATION_POLL_MS);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, report?.status, t]);

  // 获取检查项列表
  const fetchChecks = async (visitId) => {
    if (!visitId) {
      setChecksError(t('reports.noVisitId'));
      setChecksLoading(false);
      setChecksReady(true); // 没有visitId时也设为ready
      return [];
    }

    try {
      setChecksLoading(true);
      setChecksReady(false); // 开始加载时设为false
      const data = await getChecksByVisitId(visitId);
      setChecks(data || []);
      setChecksError('');
      return data || []; // 👈 返回数据
    } catch (error) {
      console.error(`获取就诊ID${visitId}的检查项失败:`, error);
      setChecksError(t('reports.fetchChecksError'));
      return []; // 👈 出错时返回空数组
    } finally {
      setChecksLoading(false);
      setChecksReady(true); // 完成加载后设为true
    }
  };

  // 获取待检查项目列表
  const fetchPendingChecks = async (visitId) => {
    if (!visitId) {
      setPendingChecksError(t('reports.noVisitId'));
      setPendingChecksLoading(false);
      return [];
    }

    try {
      setPendingChecksLoading(true);
      const data = await getPendingChecksByVisitId(visitId);
      setPendingChecks(data || []);
      setPendingChecksError('');
      return data || []; // 👈 返回数据
    } catch (error) {
      console.error(`获取就诊ID${visitId}的待检查项失败:`, error);
      setPendingChecksError(t('reports.fetchPendingChecksError'));
      return []; // 👈 出错时返回空数组
    } finally {
      setPendingChecksLoading(false);
    }
  };

  const fetchAppointmentOrders = async (visitId) => {
    if (!visitId) {
      setAppointmentOrders([]);
      return [];
    }

    try {
      const [sourceOrders, claimOrders] = await Promise.all([
        getAppointmentOrders({ source_visit_id: visitId }),
        getAppointmentOrders({ claim_visit_id: visitId })
      ]);

      const merged = [...(sourceOrders || []), ...(claimOrders || [])];
      const uniqueOrders = merged.filter((order, index, array) => array.findIndex((candidate) => candidate.id === order.id) === index);
      setAppointmentOrders(uniqueOrders);
      return uniqueOrders;
    } catch (requestError) {
      console.error(`获取就诊ID${visitId}关联预约单失败:`, requestError);
      setAppointmentOrders([]);
      return [];
    }
  };

  // 审核待检查项目
  const handleReviewPendingCheck = async (pendingCheckId, status, note = '') => {
    try {
      setReviewSubmitting(true);
      await reviewPendingCheck(pendingCheckId, {
        status: status,
        note: note
      });
      
      // 刷新待检查项目列表
      if (report && report.visit_id) {
        fetchPendingChecks(report.visit_id);
      }
      
      setSuccessMessage(t('reports.pendingCheckReviewSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('审核待检查项目失败:', error.response?.data || error.message || error);
      setError(t('reports.pendingCheckReviewError'));
    } finally {
      setReviewSubmitting(false);
    }
  };

  // 创建待检查项目
  const handleCreatePendingCheck = async () => {
    if (!createPendingCheckData.check_type || !createPendingCheckData.description) {
      setError(t('reports.createPendingCheckValidationError'));
      return;
    }

    if (!report || !report.visit_id) {
      setError(t('reports.noVisitIdForPendingCheck'));
      return;
    }

    try {
      setCreatingPendingCheck(true);
      setError('');

      // 将用户ID填入review_note字段以标识这是用户创建的待检查项
      await createPendingCheck({
        visit_id: report.visit_id,
        check_type: createPendingCheckData.check_type,
        description: createPendingCheckData.description,
        review_note: currentUser?.id ? String(currentUser.id) : 'user_created'
      });

      // 刷新待检查项目列表
      fetchPendingChecks(report.visit_id);
      
      // 重置表单和关闭对话框
      setCreatePendingCheckData({
        check_type: '',
        description: ''
      });
      setShowCreatePendingCheck(false);
      
      setSuccessMessage(t('reports.createPendingCheckSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('创建待检查项目失败:', error.response?.data || error.message || error);
      setError(t('reports.createPendingCheckError'));
    } finally {
      setCreatingPendingCheck(false);
    }
  };

  // 删除待检查项目
  const handleDeletePendingCheck = async (pendingCheckId) => {
    // 弹出确认对话框
    if (!window.confirm(t('reports.confirmDeletePendingCheckMessage'))) {
      return;
    }

    try {
      setReviewSubmitting(true);
      await deletePendingCheck(pendingCheckId);
      
      // 刷新待检查项目列表
      if (report && report.visit_id) {
        fetchPendingChecks(report.visit_id);
      }
      
      setSuccessMessage(t('reports.deletePendingCheckSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('删除待检查项目失败:', error.response?.data || error.message || error);
      setError(t('reports.deletePendingCheckError'));
    } finally {
      setReviewSubmitting(false);
    }
  };

  const addAppointmentDraft = () => {
    setAppointmentDrafts((current) => [...current, { check_type: '', description: '' }]);
  };

  const updateAppointmentDraft = (index, field, value) => {
    setAppointmentDrafts((current) =>
      current.map((draft, draftIndex) => (
        draftIndex === index ? { ...draft, [field]: value } : draft
      ))
    );
  };

  const removeAppointmentDraft = (index) => {
    setAppointmentDrafts((current) => {
      if (current.length === 1) {
        return [{ check_type: '', description: '' }];
      }
      return current.filter((_, draftIndex) => draftIndex !== index);
    });
  };

  const addExaminationDraft = () => {
    setExaminationDrafts((current) => [...current, { check_type: '', description: '' }]);
  };

  const updateExaminationDraft = (index, field, value) => {
    setExaminationDrafts((current) =>
      current.map((draft, draftIndex) => (
        draftIndex === index ? { ...draft, [field]: value } : draft
      ))
    );
  };

  const removeExaminationDraft = (index) => {
    setExaminationDrafts((current) => {
      if (current.length === 1) {
        return [{ check_type: '', description: '' }];
      }
      return current.filter((_, draftIndex) => draftIndex !== index);
    });
  };

  const addPrescriptionDraft = () => {
    setPrescriptionDrafts((current) => [...current, createEmptyPrescriptionDraft()]);
    setInvalidPrescriptionRows([]);
  };

  const updatePrescriptionDraft = (index, field, value) => {
    setPrescriptionDrafts((current) =>
      current.map((draft, draftIndex) => (
        draftIndex === index ? { ...draft, [field]: value } : draft
      ))
    );
    setInvalidPrescriptionRows([]);
  };

  const removePrescriptionDraft = (index) => {
    setPrescriptionDrafts((current) => {
      if (current.length === 1) {
        return [createEmptyPrescriptionDraft()];
      }
      return current.filter((_, draftIndex) => draftIndex !== index);
    });
    setInvalidPrescriptionRows([]);
  };

  // 获取对话消息列表
  const fetchConversations = async (visitId) => {
    if (!visitId) {
      setConversationsError(t('reports.noVisitId'));
      return;
    }

    try {
      setConversationsLoading(true);
      setConversationsError('');
      const data = await getConversationMessagesByVisitId(visitId);

      // 过滤掉以"人类医生已确认如下报告"开头的消息
      const filteredMessages = (data.messages || []).filter(message => {
        return !message.content || !message.content.startsWith('人类医生已确认如下报告');
      });

      setConversations(filteredMessages);
    } catch (error) {
      console.error(`获取就诊ID${visitId}的对话消息失败:`, error);
      setConversationsError(t('reports.fetchConversationError'));
    } finally {
      setConversationsLoading(false);
    }
  };

  // 处理审核操作的通用函数
  const handleReview = async () => {
    if (report?.status !== 'pending') {
      setError(t('reports.reportNotReadyForReview'));
      return;
    }

    const normalizedNote = note.trim();
    if (!normalizedNote) {
      setError(t('reports.reviewNoteRequiredHint'));
      return;
    }

    const isFinalReport = report?.report_type === 'final';
    const preparedExaminationDrafts = prepareMedicalOrderDrafts(examinationDrafts);
    if (isFinalReport && preparedExaminationDrafts.hasMissingCheckType) {
      setError(t('reports.examinationOrdersMissingType'));
      return;
    }

    const examinationOrdersPayload = isFinalReport ? preparedExaminationDrafts.orders : [];
    const preparedPrescriptionDrafts = preparePrescriptionDrafts(prescriptionDrafts);
    if (isFinalReport && preparedPrescriptionDrafts.hasIncompleteRows) {
      setInvalidPrescriptionRows(preparedPrescriptionDrafts.incompleteRows);
      setError(t('reports.prescriptionIncompleteRowError'));
      window.setTimeout(() => prescriptionEditorRef.current?.focusFirstInvalid(), 0);
      return;
    }

    setInvalidPrescriptionRows([]);
    const prescriptionItemsPayload = isFinalReport ? preparedPrescriptionDrafts.items : [];
    const appointmentOrdersPayload = isFinalReport
      ? prepareMedicalOrderDrafts(appointmentDrafts).orders
      : [];
    
    setSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      // 先更新病历内容
      await updateReportContent(id, reportContent);

      // 然后提交审核结果
      const profileOperationsPayload = (
        report?.report_type === 'final'
          ? buildProfileReviewOperations()
          : []
      );

      const reviewResult = await reviewReport(id, {
        status: 'approved',
        note: normalizedNote,
        content: reportContent,
        examination_orders: examinationOrdersPayload,
        prescription_items: prescriptionItemsPayload,
        appointment_orders: appointmentOrdersPayload,
        patient_profile_operations: profileOperationsPayload
      });

      if (reviewResult?.next_action === 'review_final' && reviewResult?.next_report_id) {
        setSuccessMessage(t('reports.finalReportQueued', { reportId: reviewResult.next_report_id }));
        setTimeout(() => {
          navigate(`/reports/${reviewResult.next_report_id}/review`);
        }, 800);
        return;
      }

      setSuccessMessage(t('reports.reviewSuccess'));
      setTimeout(() => {
        navigate('/reports');
      }, 2000);
    } catch (error) {
      console.error('审核报告失败:', error.response?.data || error.message || error);
      setError(t('reports.reviewError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await handleReview();
  };

  // 首次生成由后端自动排队；这里只允许人工重试最终失败的任务。
  const retryGeneration = async () => {
    setGenerating(true);
    setError('');
    setSuccessMessage(t('reports.generationQueued'));

    try {
      const result = await retryReportGeneration(id);
      if (result?.report) {
        setReport(result.report);
        if (typeof result.report.content === 'string') {
          setReportContent(result.report.content);
        }
      }

      if (result?.report?.status === 'generating') {
        setGenerating(true);
        setSuccessMessage(t('reports.generationQueued'));
      } else {
        setGenerating(false);
        if (result?.report?.content) {
          setSuccessMessage(t('reports.generationSuccess'));
        }
      }
    } catch (error) {
      setGenerating(false);
      console.error('启动报告生成失败:', error.response?.data || error.message || error);
      setError(t('reports.generationError'));
    }
  };

  // 导出 Markdown 文件
  const handleExportMarkdown = () => {
    if (!reportContent || reportContent.trim() === '') {
      setError(t('reports.noContentToExport'));
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      // 创建 Blob 对象
      const blob = new Blob([reportContent], { type: 'text/markdown;charset=utf-8' });
      
      // 创建下载链接
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // 生成文件名：report_[报告ID]_[时间戳].md
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `report_${id}_${timestamp}.md`;
      
      // 触发下载
      document.body.appendChild(link);
      link.click();
      
      // 清理
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setSuccessMessage(t('reports.exportSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('导出Markdown文件失败:', error.response?.data || error.message || error);
      setError(t('reports.exportError'));
      setTimeout(() => setError(''), 3000);
    }
  };

  // 导出 PDF 文件（使用浏览器打印功能）
  const handleExportPDF = () => {
    if (!reportContent || reportContent.trim() === '') {
      setError(t('reports.noContentToExport'));
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      // 创建一个新窗口用于打印
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        setError(t('reports.exportError'));
        setTimeout(() => setError(''), 3000);
        return;
      }

      // 渲染Markdown为HTML
      const markdownContent = document.querySelector('.markdown-content');
      const htmlContent = markdownContent ? markdownContent.innerHTML : reportContent;

      // 生成打印页面的HTML
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="${reportLocale}" dir="${reportDirection}">
        <head>
          <meta charset="utf-8">
          <title>${t('reports.reportContent')} #${id} - ${timestamp}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Microsoft YaHei", "微软雅黑";
              line-height: 1.6;
              color: #333;
              max-width: 800px;
              margin: 20px auto;
              padding: 20px;
              text-align: start;
            }
            h1 {
              color: #2c3e50;
              font-size: 1.8em;
              margin-top: 1.5em;
              margin-bottom: 0.8em;
              padding-bottom: 0.3em;
              border-bottom: 1px solid #eaecef;
            }
            h2 {
              color: #3498db;
              font-size: 1.5em;
              margin-top: 1.2em;
              margin-bottom: 0.6em;
            }
            h3 {
              color: #2980b9;
              font-size: 1.3em;
              margin-top: 1em;
              margin-bottom: 0.5em;
            }
            p {
              margin-top: 0.5em;
              margin-bottom: 1em;
            }
            ul, ol {
              margin-top: 0.5em;
              margin-bottom: 1em;
              padding-inline-start: 2em;
            }
            li {
              margin-bottom: 0.3em;
            }
            table {
              border-collapse: collapse;
              width: 100%;
              margin: 1em 0;
            }
            table th {
              background-color: #f8f9fa;
              border: 1px solid #dfe2e5;
              padding: 0.5em 1em;
              font-weight: 600;
            }
            table td {
              border: 1px solid #dfe2e5;
              padding: 0.5em 1em;
            }
            @media print {
              body {
                margin: 0;
                padding: 15mm;
              }
            }
          </style>
        </head>
        <body>
          <h1>${t('reports.reportContent')} #${id}</h1>
          <p style="color: #666; font-size: 0.9em;">${t('reports.createdAt')}: ${formatReportDateTime(new Date(), reportLocale)}</p>
          <hr style="margin: 20px 0; border: none; border-top: 2px solid #eee;">
          ${htmlContent}
        </body>
        </html>
      `);
      printWindow.document.close();

      // 等待内容加载完成后触发打印
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 250);
      };

      setSuccessMessage(t('reports.exportSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('导出PDF失败:', error.response?.data || error.message || error);
      setError(t('reports.exportError'));
      setTimeout(() => setError(''), 3000);
    }
  };

  // 导出 DOCX 文件
  const handleExportDOCX = () => {
    if (!reportContent || reportContent.trim() === '') {
      setError(t('reports.noContentToExport'));
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      // 将Markdown转换为简单的HTML格式
      let htmlContent = reportContent
        // 标题转换
        .replace(/^### (.*$)/gim, '<h3>$1</h3>')
        .replace(/^## (.*$)/gim, '<h2>$1</h2>')
        .replace(/^# (.*$)/gim, '<h1>$1</h1>')
        // 粗体和斜体
        .replace(/\*\*\*(.*?)\*\*\*/g, '<b><i>$1</i></b>')
        .replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
        .replace(/\*(.*?)\*/g, '<i>$1</i>')
        // 列表
        .replace(/^\* (.*$)/gim, '<li>$1</li>')
        .replace(/^- (.*$)/gim, '<li>$1</li>')
        .replace(/^\d+\. (.*$)/gim, '<li>$1</li>')
        // 段落
        .replace(/\n\n/g, '</p><p>')
        // 换行
        .replace(/\n/g, '<br/>');

      // 包装在段落标签中
      htmlContent = '<p>' + htmlContent + '</p>';
      
      // 包装列表项
      htmlContent = htmlContent.replace(/(<li>.*?<\/li>)/gs, (match) => {
        return '<ul>' + match + '</ul>';
      });

      // 创建完整的HTML文档
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const fullHTML = `
        <!DOCTYPE html>
        <html lang='${reportLocale}' dir='${reportDirection}' xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
          <meta charset='utf-8'>
          <title>${t('reports.reportContent')} #${id}</title>
          <style>
            body { font-family: Arial, 'Microsoft YaHei', sans-serif; line-height: 1.6; text-align: start; }
            h1 { color: #2c3e50; font-size: 24pt; margin-top: 12pt; margin-bottom: 6pt; }
            h2 { color: #3498db; font-size: 18pt; margin-top: 10pt; margin-bottom: 5pt; }
            h3 { color: #2980b9; font-size: 14pt; margin-top: 8pt; margin-bottom: 4pt; }
            p { margin-top: 6pt; margin-bottom: 6pt; }
            ul, ol { margin-top: 6pt; margin-bottom: 6pt; }
            li { margin-bottom: 3pt; }
          </style>
        </head>
        <body>
          <h1>${t('reports.reportContent')} #${id}</h1>
          <p style="color: #666; font-size: 10pt;">${t('reports.createdAt')}: ${formatReportDateTime(new Date(), reportLocale)}</p>
          <hr/>
          ${htmlContent}
        </body>
        </html>
      `;

      // 创建Blob对象
      const blob = new Blob(['\ufeff', fullHTML], {
        type: 'application/msword'
      });

      // 创建下载链接
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report_${id}_${timestamp}.doc`;

      // 触发下载
      document.body.appendChild(link);
      link.click();

      // 清理
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccessMessage(t('reports.exportSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('导出DOCX失败:', error.response?.data || error.message || error);
      setError(t('reports.exportError'));
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleGenerateReport = async () => {
    if (!report) {
      setError(t('reports.noReportData'));
      return;
    }
    if (isReportGenerating) {
      setSuccessMessage(t('reports.generationAlreadyRunning'));
      return;
    }
    if (report.status !== 'generation_failed') {
      setError(t('reports.reportNotReadyForReview'));
      return;
    }

    await retryGeneration();
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('reports.notSpecified');
    return formatDateTime(dateString, uiLocale) || t('reports.notSpecified');
  };

  const startContentEdit = () => {
    if (!isReportEditable) {
      setError(t('reports.reportNotEditable'));
      return;
    }
    setContentBeforeEdit(reportContent);
    setViewMode('edit');
  };

  const cancelContentEdit = () => {
    setReportContent(contentBeforeEdit);
    setViewMode('preview');
  };

  const saveContentEdit = async () => {
    if (!isReportEditable) {
      setError(t('reports.reportNotEditable'));
      return;
    }

    setSavingContent(true);
    setError('');

    try {
      await updateReportContent(id, reportContent);
      setReport((currentReport) => ({ ...currentReport, content: reportContent }));
      setContentBeforeEdit(reportContent);
      setViewMode('preview');
      setSuccessMessage(t('reports.contentUpdateSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('保存病历内容失败:', error.response?.data || error.message || error);
      setError(t('reports.contentUpdateError'));
    } finally {
      setSavingContent(false);
    }
  };

  const getCheckTypeName = (type) => {
    if (!type) return t('common.unknown');
    
    // 处理 CHECKTYPES.XXX 格式的类型
    let normalizedType = type;
    if (type.startsWith('CHECKTYPES.')) {
      const typeKey = type.replace('CHECKTYPES.', '').toLowerCase();
      // 映射常见的检查类型
      const typeMapping = {
        'lab': 'lab',
        'quick': 'quick',
        'body': 'body',
        'scan': 'scan',
        'tissue': 'tissue',
        'scope': 'scope',
        'other': 'other',
        'pure_throat': 'throat',
        'physical': 'physical',
        'vital_signs': 'vital_signs',
        'urine': 'urine',
        'blood': 'blood',
        'ct': 'ct',
        'xray': 'xray',
        'ultrasound': 'ultrasound',
        'ecg': 'ecg',
        'mri': 'mri',
        'pathology': 'pathology',
        'endoscopy': 'endoscopy',
        'function': 'common',
        'heartbeat': 'heartbeat',
        'respiratory': 'respiratory',
        'temperature': 'temperature',
        'blood_pressure': 'blood'
      };
      normalizedType = typeMapping[typeKey] || typeKey;
    }
    
    return t(`checkTypes.${normalizedType}`) || type;
  };

  const toggleCheckDetails = (index) => {
    const detailsElement = document.getElementById(`check-details-${index}`);
    if (detailsElement) {
      const isVisible = detailsElement.style.display !== 'none';
      detailsElement.style.display = isVisible ? 'none' : 'block';
    }
  };

  // 按钮样式
  const getButtonStyle = (type = 'primary') => {
    const baseStyle = {
      padding: '10px 16px',
      border: '1px solid var(--report-button-border)',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      color: 'var(--report-button-text)',
      background: 'var(--report-button-bg)',
      transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease, box-shadow 120ms ease, opacity 120ms ease',
      textShadow: 'none',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: 'var(--report-button-shadow)'
    };

    const styles = {
      primary: {
        ...baseStyle,
        '--report-button-bg': 'var(--report-action-primary-bg)',
        '--report-button-hover-bg': 'var(--report-action-primary-hover-bg)',
        '--report-button-border': 'var(--report-action-primary-border)',
        '--report-button-hover-border': 'var(--report-action-primary-hover-border)',
        '--report-button-text': 'var(--report-action-primary-text)',
        '--report-button-shadow': 'var(--report-action-primary-shadow)'
      },
      success: {
        ...baseStyle,
        '--report-button-bg': 'var(--report-action-success-bg)',
        '--report-button-hover-bg': 'var(--report-action-success-hover-bg)',
        '--report-button-border': 'var(--report-action-success-border)',
        '--report-button-hover-border': 'var(--report-action-success-hover-border)',
        '--report-button-text': 'var(--report-action-success-text)',
        '--report-button-shadow': 'var(--report-action-success-shadow)'
      },
      secondary: {
        ...baseStyle,
        '--report-button-bg': 'var(--report-action-secondary-bg)',
        '--report-button-hover-bg': 'var(--report-action-secondary-hover-bg)',
        '--report-button-border': 'var(--report-action-secondary-border)',
        '--report-button-hover-border': 'var(--report-action-secondary-hover-border)',
        '--report-button-text': 'var(--report-action-secondary-text)',
        '--report-button-shadow': 'none'
      },
      dark: {
        ...baseStyle,
        '--report-button-bg': 'var(--report-action-ghost-bg)',
        '--report-button-hover-bg': 'var(--report-action-ghost-hover-bg)',
        '--report-button-border': 'var(--report-action-ghost-border)',
        '--report-button-hover-border': 'var(--report-action-ghost-hover-border)',
        '--report-button-text': 'var(--report-action-ghost-text)',
        '--report-button-shadow': 'none'
      },
      danger: {
        ...baseStyle,
        '--report-button-bg': 'var(--report-action-danger-bg)',
        '--report-button-hover-bg': 'var(--report-action-danger-hover-bg)',
        '--report-button-border': 'var(--report-action-danger-border)',
        '--report-button-hover-border': 'var(--report-action-danger-hover-border)',
        '--report-button-text': 'var(--report-action-danger-text)',
        '--report-button-shadow': 'none'
      }
    };

    return styles[type] || styles.primary;
  };

  const handleButtonHover = (e, isEnter) => {
    const button = e.currentTarget;
    button.style.background = isEnter ? 'var(--report-button-hover-bg)' : 'var(--report-button-bg)';
    button.style.borderColor = isEnter ? 'var(--report-button-hover-border)' : 'var(--report-button-border)';
  };

  // 渲染段落编辑模式
  const renderSectionEditMode = () => {
    if (editingSection === null) return null;

    const section = parsedSections[editingSection];
    if (!section) return null;

    const editorLabel = `${t('reports.editSection')}: ${section.title}`;

    return (
      <div className="report-section-editor-backdrop" role="presentation">
        <div
          className="report-section-editor-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="report-section-editor-title"
        >
          <div className="report-section-editor-modal__header">
            <h3 id="report-section-editor-title">
              {t('reports.editSection')}: {section.title}
            </h3>
            <button
              type="button"
              className="report-section-editor-modal__close"
              onClick={cancelSectionEdit}
              aria-label={t('common.close')}
              title={t('common.close')}
            >
              ×
            </button>
          </div>

          {section.titleLine && (
            <div className="report-section-editor-modal__section-title">
              <span>{t('reports.sectionTitle')}</span>
              <strong>{section.title}</strong>
            </div>
          )}

          <MarkdownRichEditor
            value={sectionContent}
            onChange={setSectionContent}
            disabled={!isReportEditable}
            height="460px"
            ariaLabel={editorLabel}
            language={reportLocale}
          />

          <div className="report-section-editor-modal__actions">
            <button
              type="button"
              onClick={cancelSectionEdit}
              style={{
                ...getButtonStyle('dark'),
                padding: '10px 20px',
                fontSize: '14px'
              }}
              onMouseEnter={(e) => handleButtonHover(e, true)}
              onMouseLeave={(e) => handleButtonHover(e, false)}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={saveSectionEdit}
              disabled={!isReportEditable}
              style={{
                ...getButtonStyle('success'),
                padding: '10px 20px',
                fontSize: '14px',
                opacity: !isReportEditable ? 0.45 : 1,
                cursor: !isReportEditable ? 'not-allowed' : 'pointer'
              }}
              onMouseEnter={(e) => isReportEditable && handleButtonHover(e, true)}
              onMouseLeave={(e) => isReportEditable && handleButtonHover(e, false)}
            >
              {t('common.save')}
            </button>
          </div>
        </div>
      </div>
    );
  };
  // 自定义ReactMarkdown组件，在h2标题旁添加编辑按钮
  const CustomH2 = ({ children, ...props }) => {
    const { ordered, ...validProps } = props;
    const titleText = children?.[0] || '';
    const sectionIndex = parsedSections.findIndex(section => section.title === titleText);

    return (
      <div className="report-markdown-heading-row" style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <h2 {...validProps} className="report-markdown-heading" style={{
          textAlign: 'start',
          color: '#3498db',
          fontWeight: '600',
          fontSize: '1.5em',
          marginTop: '1.2em',
          marginBottom: '0.6em',
          flex: 1
        }}>
          {children}
        </h2>
        {sectionIndex !== -1 && isReportEditable && (
          <button
            className="report-markdown-edit-button"
            onClick={() => startEditingSection(sectionIndex)}
            style={{
              ...getButtonStyle('secondary'),
              padding: '8px 18px',
              fontSize: '14px',
              minWidth: '80px',
              marginTop: '1.2em',
              marginBottom: '0.6em'
            }}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}
            title={t('reports.editThisSection')}
          >
            {t('common.edit')}
          </button>
        )}
      </div>
    );
  };

  const renderCheckMetadata = (check) => {
    // 检查是否有图片文件
    const hasImage = check.file_url && check.file_mime_type &&
      (check.file_mime_type.startsWith('image/') ||
        ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'].includes(check.file_mime_type.toLowerCase()));

    // 检查是否有音频文件
    const hasAudio = check.file_url && check.file_mime_type &&
      (check.file_mime_type.startsWith('audio/') ||
        ['audio/aac', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/mpeg'].includes(check.file_mime_type.toLowerCase()));

    // 处理metadata
    let metadataItems = [];
    if (check.metadata && Array.isArray(check.metadata)) {
      metadataItems = check.metadata;
    } else if (check.metadata && typeof check.metadata === 'object') {
      metadataItems = Object.entries(check.metadata).map(([key, value]) => ({
        name: key,
        value: value
      }));
    }

    const hasMetadata = metadataItems.length > 0;

    // 如果既没有图片、音频也没有metadata，显示无数据
    if (!hasImage && !hasAudio && !hasMetadata) {
      return <p style={{ color: '#334255' }}>{t('reports.noMetadata')}</p>;
    }

    return (
      <div className="check-metadata">
        {/* 显示图片 */}
        {hasImage && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ marginBottom: '10px', color: '#2c3e50' }}>{t('reports.checkImage')}</h4>
            <div style={{
              border: '2px solid #dde6e9',
              borderRadius: '8px',
              padding: '10px',
              backgroundColor: '#f8f9fa',
              textAlign: 'center'
            }}>
              <img
                src={check.file_url}
                alt={`${getCheckTypeName(check.check_type)} 检查图片`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div style={{ display: 'none', color: '#556476', marginTop: '10px' }}>
                {t('reports.imageLoadError')}
              </div>
              <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#556476' }}>
                {t('reports.fileType')}: {check.file_mime_type}
              </div>
            </div>
          </div>
        )}

        {/* 显示音频 */}
        {hasAudio && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ marginBottom: '10px', color: '#2c3e50' }}>{t('reports.checkAudio')}</h4>
            <div style={{
              border: '2px solid #dde6e9',
              borderRadius: '8px',
              padding: '15px',
              backgroundColor: '#f8f9fa'
            }}>
              <audio
                controls
                style={{
                  width: '100%',
                  marginBottom: '10px'
                }}
                preload="metadata"
              >
                <source src={check.file_url} type={check.file_mime_type} />
                {t('reports.audioNotSupported')}
              </audio>
              <div style={{ fontSize: '0.9em', color: '#556476' }}>
                {t('reports.fileType')}: {check.file_mime_type}
              </div>
              <div style={{ fontSize: '0.9em', color: '#556476', marginTop: '5px' }}>
                <a
                  href={check.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#1d6a86',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  {t('reports.downloadAudio')}
                </a>
              </div>
            </div>
          </div>
        )}

        {/* 显示metadata */}
        {hasMetadata && (
          <div>
            <h4 style={{ color: '#14253a' }}>{t('reports.checkItemData')}</h4>
            <table className="paper-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{
                    textAlign: 'start',
                    padding: '8px',
                    backgroundColor: '#eef7fa',
                    border: '1px solid #dde6e9',
                    fontWeight: '600'
                  }}>
                    {t('reports.name')}
                  </th>
                  <th style={{
                    textAlign: 'start',
                    padding: '8px',
                    backgroundColor: '#eef7fa',
                    border: '1px solid #dde6e9',
                    fontWeight: '600'
                  }}>
                    {t('reports.value')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {metadataItems.map((item, idx) => (
                  <tr key={idx}>
                    <td style={{
                      padding: '8px',
                      border: '1px solid #dde6e9'
                    }}>
                      {item.name_local || item.name}
                    </td>
                    <td style={{
                      padding: '8px',
                      border: '1px solid #dde6e9'
                    }}>
                      {item.value !== undefined && item.value !== null
                        ? <span>
                          {String(item.value)}
                          {item.unit ? ` ${item.unit}` : ''}
                          {item.reference_range ? ` (${t('reports.referenceRange')}: ${item.reference_range})` : ''}
                        </span>
                        : t('reports.noData')
                      }
                      {item.description_local || item.description
                        ? <div style={{ marginTop: '5px', color: '#556476', fontSize: '0.9em' }}>
                          {item.description_local || item.description}
                        </div>
                        : null
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // 格式化报告类型显示
  const formatReportType = (reportType) => {
    if (!reportType) return '';
    
    if (reportType === 'final' && (report?.workflow === 'single_review' || report?.report_flow_version === 2)) {
      return t('reports.completeClinicalRecord');
    }

    const typeMap = {
      'proposal': t('reports.proposalReport'),
      'final': t('reports.finalReport')
    };
    
    return typeMap[reportType] || reportType;
  };

  const formatVisitType = (visitType) => {
    if (visitType === 'follow_up') {
      return t('reports.visitTypeFollowUp');
    }
    if (visitType === 'initial') {
      return t('reports.visitTypeInitial');
    }
    if (visitType === 'checkup') {
      return t('reports.visitTypeCheckup');
    }
    return t('reports.visitTypeUnknown');
  };

  const formatRelatedVisits = (relatedVisitIds) => {
    if (!relatedVisitIds || relatedVisitIds.length === 0) {
      return t('reports.noLinkedSourceVisits');
    }

    return relatedVisitIds.map((visitId) => `#${visitId}`).join(' / ');
  };

  const formatClinicLabel = (name, clinicId) => {
    if (name) {
      return name;
    }
    if (clinicId) {
      return `Clinic #${clinicId}`;
    }
    return t('reports.unknownClinic');
  };

  const buildRelatedVisitLink = (visitId) => `/reviewed-reports?visit_id=${visitId}`;
  const buildAppointmentQueueLink = (orderId) => `/appointment-orders?order_id=${orderId}`;
  const getAppointmentOrderDisplay = (order) => (
    order?.description || getAppointmentOrderTypeLabel(order?.check_type, t)
  );

  const getReportStatusStyle = (status) => {
    if (status === 'pending') {
      return {
        background: 'rgba(241, 196, 15, 0.1)',
        color: '#f39c12',
        border: '1px solid rgba(241, 196, 15, 0.3)'
      };
    }
    if (status === 'approved') {
      return {
        background: 'rgba(46, 204, 113, 0.1)',
        color: '#27ae60',
        border: '1px solid rgba(46, 204, 113, 0.3)'
      };
    }
    if (status === 'generating') {
      return {
        background: 'rgba(52, 152, 219, 0.1)',
        color: '#2980b9',
        border: '1px solid rgba(52, 152, 219, 0.3)'
      };
    }
    return {
      background: 'rgba(231, 76, 60, 0.1)',
      color: '#e74c3c',
      border: '1px solid rgba(231, 76, 60, 0.3)'
    };
  };

  const isReportGenerating = report?.status === 'generating' || generating;
  const canReviewFromWorkflow = !Array.isArray(report?.available_actions) || report.available_actions.includes('review');
  const isReportEditable = report?.status === 'pending' && canReviewFromWorkflow;
  const isReportReviewable = report?.status === 'pending' && canReviewFromWorkflow;
  const hasRequiredReviewNote = note.trim().length > 0;

  const renderPatientProfileEditor = () => {
    if (!(report?.report_type === 'final')) {
      return null;
    }

    const canEditProfile = isReportEditable;
    const groupedFacts = PROFILE_CATEGORIES.map((category) => ({
      ...category,
      facts: profileDraftFacts.filter((fact) => fact.category === category.value)
    })).filter((group) => group.facts.length > 0);

    return (
      <div style={{
        marginBottom: '24px',
        padding: '20px',
        borderRadius: '14px',
        background: 'rgba(255, 255, 255, 0.56)',
        border: '1px solid rgba(0, 0, 0, 0.08)'
      }}>
        <div className="report-section-title-row" style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start', marginBottom: '16px' }}>
          <h3>
            {t('reports.profileTitle')}
          </h3>

          {canEditProfile && (
            <button
              type="button"
              onClick={addProfileDraftFact}
              style={{
                ...getButtonStyle('secondary'),
                padding: '9px 16px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => handleButtonHover(e, true)}
              onMouseLeave={(e) => handleButtonHover(e, false)}
            >
              {t('reports.profileAdd')}
            </button>
          )}
        </div>

        {profileLoading ? (
          <div style={{ color: '#556476', fontSize: '14px', padding: '16px 0' }}>
            {t('reports.profileLoading')}
          </div>
        ) : profileError ? (
          <div style={{
            color: '#b42318',
            background: 'rgba(244, 67, 54, 0.08)',
            border: '1px solid rgba(244, 67, 54, 0.18)',
            borderRadius: '8px',
            padding: '12px',
            fontSize: '14px',
            marginBottom: '12px'
          }}>
            {profileError}
          </div>
        ) : null}

        {!profileLoading && profileDraftFacts.length === 0 ? (
          <div style={{
            color: '#667085',
            background: 'rgba(20, 37, 58, 0.04)',
            borderRadius: '8px',
            padding: '16px',
            fontSize: '14px'
          }}>
            {t('reports.profileEmpty')}
          </div>
        ) : null}

        <div style={{ display: 'grid', gap: '16px' }}>
          {(groupedFacts.length > 0 ? groupedFacts : [{ value: 'other_history', facts: profileDraftFacts }]).map((group) => (
            <div key={`profile-group-${group.value}`} style={{ display: group.facts.length > 0 ? 'grid' : 'none', gap: '10px' }}>
              <div style={{
                color: '#34495e',
                fontWeight: '700',
                fontSize: '14px',
                paddingBottom: '2px',
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
              }}>
                {getProfileCategoryLabel(group.value)}
              </div>

              {group.facts.map((fact) => {
                const factKey = getProfileFactKey(fact);
                return (
                  <div
                    key={`profile-fact-${factKey}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: canEditProfile ? 'minmax(9rem, 12rem) minmax(8rem, 1fr) minmax(12rem, 1.4fr) auto' : 'minmax(9rem, 12rem) minmax(8rem, 1fr) minmax(12rem, 1.4fr)',
                      gap: '10px',
                      alignItems: 'start'
                    }}
                  >
                    <select
                      value={fact.category}
                      disabled={!canEditProfile}
                      onChange={(event) => updateProfileDraftFact(factKey, 'category', event.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid rgba(102, 126, 234, 0.22)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        background: canEditProfile ? 'rgba(255, 248, 241, 0.88)' : 'rgba(20, 37, 58, 0.04)',
                        color: '#1a202c',
                        boxSizing: 'border-box'
                      }}
                    >
                      {PROFILE_CATEGORIES.map((category) => (
                        <option key={category.value} value={category.value}>
                          {t(`patients.profileCategories.${category.value}`)}
                        </option>
                      ))}
                    </select>

                    <input
                      type="text"
                      value={fact.label}
                      disabled={!canEditProfile}
                      onChange={(event) => updateProfileDraftFact(factKey, 'label', event.target.value)}
                      placeholder={t('reports.profileLabelPlaceholder')}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid rgba(102, 126, 234, 0.22)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        background: canEditProfile ? 'rgba(255, 248, 241, 0.88)' : 'rgba(20, 37, 58, 0.04)',
                        color: '#1a202c',
                        boxSizing: 'border-box'
                      }}
                    />

                    <div style={{ display: 'grid', gap: '6px' }}>
                      <input
                        type="text"
                        value={fact.value}
                        disabled={!canEditProfile}
                        onChange={(event) => updateProfileDraftFact(factKey, 'value', event.target.value)}
                        placeholder={t('reports.profileValuePlaceholder')}
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid rgba(102, 126, 234, 0.22)',
                          borderRadius: '8px',
                          fontSize: '14px',
                          background: canEditProfile ? 'rgba(255, 248, 241, 0.88)' : 'rgba(20, 37, 58, 0.04)',
                          color: '#1a202c',
                          boxSizing: 'border-box'
                        }}
                      />
                      <span style={{ color: '#667085', fontSize: '12px' }}>
                        {getProfileStatusLabel(fact.status)}
                        {fact.last_confirmed_at ? ` | ${t('reports.profileConfirmedAt', { date: formatDate(fact.last_confirmed_at) })}` : ''}
                        {!fact.last_confirmed_at && fact.last_reported_at ? ` | ${t('reports.profileReportedAt', { date: formatDate(fact.last_reported_at) })}` : ''}
                      </span>
                    </div>

                    {canEditProfile && (
                      <button
                        type="button"
                        onClick={() => removeProfileDraftFact(fact)}
                        style={{
                          ...getButtonStyle('danger'),
                          padding: '9px 14px',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => handleButtonHover(e, true)}
                        onMouseLeave={(e) => handleButtonHover(e, false)}
                      >
                        {t('reports.remove')}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'transparent',
        padding: '20px',
        position: 'relative'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '50px',
          maxWidth: '1200px',
          margin: '0 auto',
          paddingTop: '100px'
        }}>
          <div className="loading-spinner" style={{
            display: 'inline-block',
            width: '40px',
            height: '40px',
            border: '4px solid rgba(0,0,0,.1)',
            borderRadius: '50%',
            borderTopColor: '#667eea',
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }}></div>
          <p style={{ fontSize: '1.2rem', color: '#666' }}>{t('common.loading')}</p>

          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  return (
    <div className="report-detail-page report-detail-page--review" style={{
      minHeight: '100vh',
      background: 'transparent',
      padding: '0px',
      position: 'relative'
    }}>
      {/* Back按钮区域 - 左上角 */}
      <div className="report-detail-page__back" style={{
        position: 'absolute',
        top: '0px',
        insetInlineStart: '0px',
        zIndex: 1000
      }}>
        <button
          onClick={() => navigate('/reports')}
          style={getButtonStyle('dark')}
          onMouseEnter={(e) => handleButtonHover(e, true)}
          onMouseLeave={(e) => handleButtonHover(e, false)}
        >
          {t('common.back')}
        </button>
      </div>

      <div className="report-detail-page__inner" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 20px',
        paddingTop: '60px'
      }}>
        {/* 标题栏 */}
        <div className="report-detail-page__header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
          background: 'rgba(250, 245, 236, 0.94)',
          padding: '20px',
          borderRadius: '16px',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.16)',
          backdropFilter: 'blur(18px)'
        }}>
          <div className="report-detail-page__title-row" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <h1 style={{
              margin: 0,
              fontSize: '2rem',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #cfac67 0%, #43d6c5 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              {(report?.workflow === 'single_review' || report?.report_flow_version === 2)
                ? t('reports.completeRecordPendingReview')
                : t('reports.reviewTitle')} #{id}
            </h1>
            {report && report.report_type && (
              <span style={{
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '600',
                background: report.report_type === 'proposal' 
                  ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                  : 'linear-gradient(135deg, rgba(46, 204, 113, 0.1) 0%, rgba(39, 174, 96, 0.1) 100%)',
                color: report.report_type === 'proposal' ? '#667eea' : '#27ae60',
                border: report.report_type === 'proposal' 
                  ? '1px solid rgba(102, 126, 234, 0.3)' 
                  : '1px solid rgba(46, 204, 113, 0.3)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {formatReportType(report.report_type)}
              </span>
            )}
            {report && (
              <span className={`status-pill ${(report.workflow === 'single_review' || report.report_flow_version === 2) ? 'status-pill--success' : 'status-pill--neutral'}`}>
                {(report.workflow === 'single_review' || report.report_flow_version === 2)
                  ? t('reports.singleReviewFlow')
                  : t('reports.legacyFlow')}
              </span>
            )}
            {report?.legacy_link_ambiguous && (
              <span className="status-pill status-pill--danger">{t('reports.ambiguousLegacyLink')}</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => {
                setLoading(true);
                setError('');
                setChecksReady(false); // 重置检查项准备状态
                const fetchReport = async () => {
                  try {
                    const data = await getReportById(id);
                    setReport(data);

                    console.log('refreshed report data', data);

                    // 如果病历内容是字符串，直接使用
                    if (typeof data.content === 'string' && data.content.trim() !== '') {
                      setReportContent(data.content);
                    } else if (data.content && typeof data.content === 'object' && Object.keys(data.content).length > 0) {
                      // 如果病历内容是非空对象，转换为JSON字符串
                      setReportContent(JSON.stringify(data.content, null, 2));
                    } else {
                      // 如果病历内容为空，设置为空字符串
                      setReportContent('');
                    }

                    // 获取相关的检查项
                    if (data.visit_id) {
                      await fetchChecks(data.visit_id);
                      
                      // 获取待检查项目 - 添加 await 确保数据加载完成
                      await fetchPendingChecks(data.visit_id);
                      await fetchAppointmentOrders(data.visit_id);
                      try {
                        const visitData = await getVisitById(data.visit_id);
                        setVisit(visitData);
                        await fetchPatientProfile(visitData.patient_id);
                      } catch (visitError) {
                        console.error(`刷新时获取就诊信息失败:`, visitError);
                      }
                    } else {
                      setChecksReady(true); // 如果没有visit_id，直接设为ready
                      setVisit(null);
                      resetProfileDraft(null);
                    }

                    setLoading(false);
                  } catch (error) {
                    console.error('获取报告详情失败:', error.response?.data || error.message || error);
                    setError(t('reports.fetchReportError'));
                    setChecksReady(true); // 即使出错也设为ready，避免按钮永远不显示
                    setLoading(false);
                  }
                };
                fetchReport();
              }}
              style={getButtonStyle('success')}
              onMouseEnter={(e) => handleButtonHover(e, true)}
              onMouseLeave={(e) => handleButtonHover(e, false)}
            >
              {t('common.refresh')}
            </button>
          </div>
        </div>

        {error && (
          <div style={{
            color: '#ff416c',
            marginBottom: '20px',
            textAlign: 'center',
            background: 'rgba(255, 65, 108, 0.1)',
            padding: '15px',
            borderRadius: '8px',
            border: '1px solid rgba(255, 65, 108, 0.3)',
            fontWeight: '500'
          }}>
            {error}
          </div>
        )}

        {successMessage && (
          <div style={{
            color: '#27ae60',
            marginBottom: '20px',
            textAlign: 'center',
            background: 'rgba(46, 204, 113, 0.1)',
            padding: '15px',
            borderRadius: '8px',
            border: '1px solid rgba(46, 204, 113, 0.3)',
            fontWeight: '500'
          }}>
            {successMessage}
          </div>
        )}

        <div className="report-detail-page__section" style={{
          marginBottom: '20px',
          padding: '20px',
          background: 'rgba(250, 245, 236, 0.94)',
          borderRadius: '16px',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.16)',
          backdropFilter: 'blur(18px)'
        }}>
          <h3 style={{
            margin: '0 0 20px 0',
            fontSize: '1.5rem',
            fontWeight: '600',
            background: 'linear-gradient(135deg, #cfac67 0%, #43d6c5 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            {t('reports.reportInfo')}
          </h3>
          <div className="report-info-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px' }}>
              <strong style={{ color: '#2c3e50' }}>{t('reports.reportId')}:</strong>
              <span style={{ marginInlineStart: '8px', color: '#34495e' }}>{report.id}</span>
            </p>
            <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px' }}>
              <strong style={{ color: '#2c3e50' }}>{t('reports.status')}:</strong>
              <span style={{
                marginInlineStart: '8px',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '600',
                ...getReportStatusStyle(report.status)
              }}>
                {t(`reports.${report.status}`)}
              </span>
            </p>
            <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px' }}>
              <strong style={{ color: '#2c3e50' }}>{t('reports.clinicId')}:</strong>
              <span style={{ marginInlineStart: '8px', color: '#34495e' }}>{report.clinic_id || t('reports.notSpecified')}</span>
            </p>
            <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px' }}>
              <strong style={{ color: '#2c3e50' }}>{t('reports.clinicName')}:</strong>
              <span style={{ marginInlineStart: '8px', color: '#34495e' }}>{report.clinic_name || t('reports.notSpecified')}</span>
            </p>
            <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px' }}>
              <strong style={{ color: '#2c3e50' }}>{t('reports.visitId')}:</strong>
              <span style={{ marginInlineStart: '8px', color: '#34495e' }}>{report.visit_id}</span>
            </p>
            <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px' }}>
              <strong style={{ color: '#2c3e50' }}>{t('reports.visitType')}:</strong>
              <span style={{ marginInlineStart: '8px', color: '#34495e' }}>{formatVisitType(report.visit_type)}</span>
            </p>
            <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px' }}>
              <strong style={{ color: '#2c3e50' }}>{t('reports.workflow')}:</strong>
              <span style={{ marginInlineStart: '8px', color: '#34495e' }}>
                {(report.workflow === 'single_review' || report.report_flow_version === 2)
                  ? t('reports.singleReviewFlow')
                  : t('reports.legacyFlow')}
              </span>
            </p>
            <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px' }}>
              <strong style={{ color: '#2c3e50' }}>{t('reports.stage')}:</strong>
              <span style={{ marginInlineStart: '8px', color: '#34495e' }}>
                {formatReportType(report.stage || report.report_type)}
                {report.revision_number ? ` · ${t('reports.revision')} ${report.revision_number}` : ''}
              </span>
            </p>
            <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px' }}>
              <strong style={{ color: '#2c3e50' }}>{t('reports.createdAt')}:</strong>
              <span style={{ marginInlineStart: '8px', color: '#34495e' }}>{formatDate(report.created_at)}</span>
            </p>
            <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px' }}>
              <strong style={{ color: '#2c3e50' }}>{t('reports.relatedVisits')}:</strong>
              <span style={{ marginInlineStart: '8px', color: '#34495e' }}>{formatRelatedVisits(report.related_visit_ids)}</span>
            </p>
            {report.related_visit_ids?.length > 0 && (
              <div className="visit-chip-list" style={{ paddingInlineStart: '15px', marginTop: '2px' }}>
                {report.related_visit_ids.map((visitId) => (
                  <Link key={`report-review-related-${visitId}`} to={buildRelatedVisitLink(visitId)} className="visit-chip">
                    #{visitId}
                  </Link>
                ))}
              </div>
            )}
            {(report.predecessor_report_id || report.successor_report_id) && (
              <div className="visit-chip-list" style={{ paddingInlineStart: '15px', marginTop: '2px' }}>
                {report.predecessor_report_id && (
                  <Link to={`/reports/${report.predecessor_report_id}/view`} className="visit-chip">
                    {t('reports.previousStage')} #{report.predecessor_report_id}
                  </Link>
                )}
                {report.successor_report_id && (
                  <Link to={`/reports/${report.successor_report_id}/review`} className="visit-chip">
                    {t('reports.nextStage')} #{report.successor_report_id}
                  </Link>
                )}
              </div>
            )}
            {report.legacy_link_ambiguous && (
              <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px', color: '#b42318' }}>
                {t('reports.ambiguousLegacyLinkHint')}
              </p>
            )}
          </div>

          {appointmentOrders.length > 0 && (
            <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
              <h4 className="report-subsection-title" style={{ marginBottom: '12px' }}>
                {t('reports.linkedAppointmentOrders')}
              </h4>
              <div className="mini-card-grid">
                {appointmentOrders.map((order) => (
                  <div
                    key={order.id}
                    className="mini-card"
                    style={{ background: 'rgba(255, 255, 255, 0.55)', borderColor: 'rgba(0, 0, 0, 0.08)' }}
                  >
                    <div className="mini-card__header">
                      <span className="mini-card__title" style={{ color: '#14253a' }}>
                        {getAppointmentOrderDisplay(order)}
                      </span>
                      <span style={{ color: '#667085', fontSize: '13px' }}>#{order.id}</span>
                    </div>
                    <div className="mini-card__meta" style={{ color: '#556476' }}>
                      {t('reports.sourceClinic')}: {formatClinicLabel(order.source_clinic_name, order.source_clinic_id)}
                    </div>
                    <div className="mini-card__meta" style={{ color: '#556476' }}>
                      {t('reports.claimClinic')}: {formatClinicLabel(order.claim_clinic_name, order.claim_clinic_id)}
                    </div>
                    <div className="inline-link-row" style={{ marginTop: '0.15rem' }}>
                      <Link className="inline-link" to={buildAppointmentQueueLink(order.id)}>
                        {t('reports.openAppointmentQueue')}
                      </Link>
                      <Link className="inline-link" to={buildRelatedVisitLink(order.source_visit_id)}>
                        {t('reports.openSourceVisit')}
                      </Link>
                      {order.source_report_id && (
                        <Link className="inline-link" to={`/reports/${order.source_report_id}/view`}>
                          {t('reports.openSourceReport')}
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {renderPatientProfileEditor()}

        <div className="report-detail-page__section report-detail-page__section--document" style={{
          marginBottom: '20px',
          background: 'rgba(250, 245, 236, 0.94)',
          borderRadius: '16px',
          padding: '30px',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.16)',
          backdropFilter: 'blur(18px)'
        }}>
          <div className="report-section-title-row report-section-title-row--document" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{
              margin: 0,
              fontSize: '1.5rem',
              fontWeight: '600',
              background: 'linear-gradient(135deg, #cfac67 0%, #43d6c5 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              {t('reports.reportContent')}
            </h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              {showOpsMarkdownTools && (
                <button
                  type="button"
                  onClick={handleExportMarkdown}
                  disabled={!reportContent || reportContent.trim() === ''}
                  style={{
                    ...getButtonStyle('secondary'),
                    opacity: (!reportContent || reportContent.trim() === '') ? 0.4 : 1,
                    cursor: (!reportContent || reportContent.trim() === '') ? 'not-allowed' : 'pointer'
                  }}
                  onMouseEnter={(e) => (reportContent && reportContent.trim() !== '') && handleButtonHover(e, true)}
                  onMouseLeave={(e) => (reportContent && reportContent.trim() !== '') && handleButtonHover(e, false)}
                  title={t('reports.exportMarkdown')}
                >
                  📥 MD
                </button>
              )}

              <button
                type="button"
                onClick={handleExportPDF}
                disabled={!reportContent || reportContent.trim() === ''}
                style={{
                  ...getButtonStyle('secondary'),
                  opacity: (!reportContent || reportContent.trim() === '') ? 0.4 : 1,
                  cursor: (!reportContent || reportContent.trim() === '') ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => (reportContent && reportContent.trim() !== '') && handleButtonHover(e, true)}
                onMouseLeave={(e) => (reportContent && reportContent.trim() !== '') && handleButtonHover(e, false)}
                title={t('reports.exportPdf')}
              >
                📄 PDF
              </button>

              <button
                type="button"
                onClick={handleExportDOCX}
                disabled={!reportContent || reportContent.trim() === ''}
                style={{
                  ...getButtonStyle('secondary'),
                  opacity: (!reportContent || reportContent.trim() === '') ? 0.4 : 1,
                  cursor: (!reportContent || reportContent.trim() === '') ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => (reportContent && reportContent.trim() !== '') && handleButtonHover(e, true)}
                onMouseLeave={(e) => (reportContent && reportContent.trim() !== '') && handleButtonHover(e, false)}
                title={t('reports.exportWord')}
              >
                📝 Word
              </button>
              
              {viewMode === 'edit' ? (
                <>
                  <button
                    type="button"
                    onClick={cancelContentEdit}
                    disabled={savingContent}
                    aria-label={t('reports.discardContentChanges')}
                    title={t('reports.discardContentChanges')}
                    style={{
                      ...getButtonStyle('dark'),
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      opacity: savingContent ? 0.45 : 1,
                      cursor: savingContent ? 'not-allowed' : 'pointer'
                    }}
                    onMouseEnter={(e) => !savingContent && handleButtonHover(e, true)}
                    onMouseLeave={(e) => !savingContent && handleButtonHover(e, false)}
                  >
                    <ArrowLeft size={16} aria-hidden="true" />
                    {t('common.back')}
                  </button>
                  <button
                    type="button"
                    onClick={saveContentEdit}
                    disabled={savingContent || !isReportEditable}
                    style={{
                      ...getButtonStyle('primary'),
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      opacity: savingContent || !isReportEditable ? 0.45 : 1,
                      cursor: savingContent || !isReportEditable ? 'not-allowed' : 'pointer'
                    }}
                    onMouseEnter={(e) => !(savingContent || !isReportEditable) && handleButtonHover(e, true)}
                    onMouseLeave={(e) => !(savingContent || !isReportEditable) && handleButtonHover(e, false)}
                    title={!isReportEditable ? t('reports.reportNotEditable') : ''}
                  >
                    <Save size={16} aria-hidden="true" />
                    {t('common.save')}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={startContentEdit}
                  disabled={!isReportEditable}
                  style={{
                    ...getButtonStyle('dark'),
                    opacity: !isReportEditable ? 0.45 : 1,
                    cursor: !isReportEditable ? 'not-allowed' : 'pointer'
                  }}
                  onMouseEnter={(e) => isReportEditable && handleButtonHover(e, true)}
                  onMouseLeave={(e) => isReportEditable && handleButtonHover(e, false)}
                  title={!isReportEditable ? t('reports.reportNotEditable') : ''}
                >
                  {t('reports.editContent')}
                </button>
              )}

              {report?.status === 'generation_failed' && !isReportGenerating && checksReady && (
                <button
                  type="button"
                  onClick={handleGenerateReport}
                  style={getButtonStyle('primary')}
                  onMouseEnter={(e) => handleButtonHover(e, true)}
                  onMouseLeave={(e) => handleButtonHover(e, false)}
                >
                  {t('reports.retryGeneration')}
                </button>
              )}

              {(!reportContent || reportContent.trim() === '') && report?.status === 'pending' && checksReady && (
                <span style={{ color: '#667085', fontWeight: '500' }}>
                  {t('reports.waitingForBackendGeneration')}
                </span>
              )}

              {(!reportContent || reportContent.trim() === '') && !isReportGenerating && !checksReady && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: '#667eea',
                  fontWeight: '500'
                }}>
                  <span>{t('reports.loadingChecks')}</span>
                  <div className="loading-spinner" style={{
                    display: 'inline-block',
                    width: '20px',
                    height: '20px',
                    border: '3px solid rgba(0,0,0,.1)',
                    borderRadius: '50%',
                    borderTopColor: '#667eea',
                    animation: 'spin 1s linear infinite',
                    marginInlineStart: '10px'
                  }}></div>
                </div>
              )}

              {isReportGenerating && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: '#667eea',
                  fontWeight: '500'
                }}>
                  <span>{t('reports.generating')}</span>
                  <div className="loading-spinner" style={{
                    display: 'inline-block',
                    width: '20px',
                    height: '20px',
                    border: '3px solid rgba(0,0,0,.1)',
                    borderRadius: '50%',
                    borderTopColor: '#667eea',
                    animation: 'spin 1s linear infinite',
                    marginInlineStart: '10px'
                  }}></div>
                </div>
              )}
            </div>
          </div>

          {viewMode === 'edit' ? (
            <MarkdownRichEditor
              value={reportContent}
              onChange={setReportContent}
              disabled={!isReportEditable || savingContent}
              height="400px"
              ariaLabel={t('reports.editReportContent')}
              language={reportLocale}
            />
          ) : (
            <div className="report-document">
              {reportContent ? (
                <div
                  className="markdown-content markdown-content--paper"
                  style={{ textAlign: 'start' }}
                  lang={reportLocale}
                  dir={reportDirection}
                >
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ node, href, children, ...props }) => {
                        const { ordered, ...validProps } = props;
                        return <a {...validProps} href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
                      },
                      pre: ({ node, children, ...props }) => {
                        const { ordered, ...validProps } = props;
                        return <pre {...validProps} style={{ margin: '1em 0' }}>{children}</pre>;
                      },
                      h1: CustomH2,
                      h2: CustomH2,
                      h3: ({ node, children, ...props }) => {
                        const { ordered, ...validProps } = props;
                        return <h3 {...validProps} style={{ textAlign: 'start', color: '#2980b9', fontWeight: '500', fontSize: '1.3em', marginTop: '1em', marginBottom: '0.5em' }}>{children}</h3>;
                      },
                      p: ({ node, children, ...props }) => {
                        const { ordered, ...validProps } = props;
                        return <p {...validProps} style={{ textAlign: 'start', marginTop: '0.5em', marginBottom: '1em', lineHeight: '1.6' }}>{children}</p>;
                      },
                      ul: ({ node, children, ...props }) => {
                        const { ordered, ...validProps } = props;
                        return <ul {...validProps} style={{ textAlign: 'start', marginTop: '0.5em', marginBottom: '1em', paddingInlineStart: '2em' }}>{children}</ul>;
                      },
                      ol: ({ node, children, ...props }) => {
                        const { ordered, ...validProps } = props;
                        return <ol {...validProps} style={{ textAlign: 'start', marginTop: '0.5em', marginBottom: '1em', paddingInlineStart: '2em' }}>{children}</ol>;
                      },
                      li: ({ node, children, ...props }) => {
                        const { ordered, ...validProps } = props;
                        return <li {...validProps} style={{ textAlign: 'start', marginBottom: '0.3em' }}>{children}</li>;
                      }
                    }}
                  >
                    {reportContent}
                  </ReactMarkdown>
                </div>
              ) : (
                <div className="report-empty-state">
                  <span>{t('reports.noContent')}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <HealthHouseCheckList visitId={report?.visit_id} />

        {/* 旧检查项列表，仅保留代码以兼容历史流程。 */}
        {SHOW_LEGACY_CHECK_SECTIONS && (
        <div className="report-detail-page__section report-checks-section" style={{
          marginBottom: '20px',
          background: 'rgba(250, 245, 236, 0.94)',
          borderRadius: '16px',
          padding: '30px',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.16)',
          backdropFilter: 'blur(18px)'
        }}>
          <h3 style={{
            margin: '0 0 20px 0',
            fontSize: '1.5rem',
            fontWeight: '600',
            background: 'linear-gradient(135deg, #cfac67 0%, #43d6c5 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            {t('reports.checksList')}
          </h3>
          <div className="checks-container">
            {checksLoading ? (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <div className="loading-spinner" style={{
                  display: 'inline-block',
                  width: '40px',
                  height: '40px',
                  border: '4px solid rgba(0,0,0,.1)',
                  borderRadius: '50%',
                  borderTopColor: '#667eea',
                  animation: 'spin 1s linear infinite',
                  marginBottom: '20px'
                }}></div>
                <p style={{ fontSize: '1.1rem', color: '#666' }}>{t('reports.loadingChecks')}</p>
              </div>
            ) : checksError ? (
              <div style={{
                color: '#ff416c',
                marginBottom: '20px',
                textAlign: 'center',
                background: 'rgba(255, 65, 108, 0.1)',
                padding: '15px',
                borderRadius: '8px',
                border: '1px solid rgba(255, 65, 108, 0.3)',
                fontWeight: '500'
              }}>
                {checksError}
              </div>
            ) : checks.length === 0 ? (
              <div className="report-empty-state">
                <span>{t('reports.noChecksData')}</span>
              </div>
            ) : (
              <div className="checks-list">
                <div style={{ overflowX: 'auto' }}>
                  <table className="paper-table report-checks-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                      }}>
                        <th style={{ padding: '15px', textAlign: 'start', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '30%', color: '#14253a' }}>
                          {t('reports.checkType')}
                        </th>
                        <th style={{ padding: '15px', textAlign: 'start', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '25%', color: '#14253a' }}>
                          {t('reports.createdAt')}
                        </th>
                        <th style={{ padding: '15px', textAlign: 'start', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '45%', color: '#14253a' }}>
                          {t('reports.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {checks.map((check, index) => (
                        <React.Fragment key={check.id || index}>
                          <tr className="check-row" style={{
                            transition: 'background-color 0.3s ease',
                            '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.05)' }
                          }}>
                            <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                              <span style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                background: 'linear-gradient(135deg, rgba(52, 152, 219, 0.1) 0%, rgba(41, 128, 185, 0.1) 100%)',
                                color: '#2980b9',
                                border: '1px solid rgba(52, 152, 219, 0.3)'
                              }}>
                                {getCheckTypeName(check.check_type)}
                              </span>
                            </td>
                            <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                              {formatDate(check.created_at)}
                            </td>
                            <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                              <button
                                className="check-toggle-btn"
                                onClick={() => toggleCheckDetails(index)}
                                style={{
                                  ...getButtonStyle('secondary'),
                                  padding: '8px 16px',
                                  fontSize: '14px',
                                  minWidth: 'auto'
                                }}
                                onMouseEnter={(e) => handleButtonHover(e, true)}
                                onMouseLeave={(e) => handleButtonHover(e, false)}
                              >
                                {t('reports.viewDetails')}
                              </button>
                            </td>
                          </tr>
                          <tr>
                            <td colSpan="3" style={{ padding: '0' }}>
                              <div
                                id={`check-details-${index}`}
                                className="check-details"
                                style={{
                                  display: 'none',
                                  padding: '20px',
                                  background: 'rgba(102, 126, 234, 0.02)',
                                  borderTop: '1px solid rgba(102, 126, 234, 0.1)'
                                }}
                              >
                                {renderCheckMetadata(check)}
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
        )}

        {/* 待检查项目列表 - 旧流程兼容，S/O Draft 页面不再展示 */}
        {SHOW_LEGACY_CHECK_SECTIONS && shouldShowPendingChecksSection() && (
          <div className="report-detail-page__section" style={{
            marginBottom: '20px',
            background: 'rgba(250, 245, 236, 0.94)',
            borderRadius: '16px',
            padding: '30px',
            boxShadow: '0 18px 48px rgba(0, 0, 0, 0.16)',
            backdropFilter: 'blur(18px)'
          }}>
            <div className="report-section-title-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {t('reports.pendingChecksList')}
              </h3>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                {/* 创建待检查项目按钮 - 仅在proposal类型且pending状态时可用 */}
                {canAddPendingCheck() ? (
                  <button
                    onClick={() => setShowCreatePendingCheck(true)}
                    style={{
                      ...getButtonStyle('primary'),
                      padding: '8px 16px',
                      fontSize: '14px',
                      minWidth: 'auto'
                    }}
                    onMouseEnter={(e) => handleButtonHover(e, true)}
                    onMouseLeave={(e) => handleButtonHover(e, false)}
                  >
                    {t('reports.addPendingCheck')}
                  </button>
                ) : (
                  <div style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    color: '#556476',
                    background: 'rgba(20, 37, 58, 0.05)',
                    borderRadius: '8px',
                    border: '1px solid rgba(20, 37, 58, 0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <path d="M15 9l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 9l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>
                      {report && report.report_type !== 'proposal' 
                        ? t('reports.addPendingCheckDisabledType')
                        : t('reports.addPendingCheckDisabledStatus')
                      }
                    </span>
                  </div>
                )}
                
                {/* 审核状态提示 */}
                {pendingChecks.length > 0 && (
                  <div style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    ...(hasPendingChecks() ? {
                      background: 'rgba(241, 196, 15, 0.1)',
                      color: '#f39c12',
                      border: '1px solid rgba(241, 196, 15, 0.3)'
                    } : {
                      background: 'rgba(46, 204, 113, 0.1)',
                      color: '#27ae60',
                      border: '1px solid rgba(46, 204, 113, 0.3)'
                    })
                  }}>
                    {hasPendingChecks() ? (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                          <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {t('reports.pendingReviewStatus', { 
                          pending: pendingChecks.filter(check => check.status === 'pending').length,
                          total: pendingChecks.length
                        })}
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        {t('reports.allChecksReviewed')}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="checks-container">
              {pendingChecksLoading ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <div className="loading-spinner" style={{
                    display: 'inline-block',
                    width: '40px',
                    height: '40px',
                    border: '4px solid rgba(0,0,0,.1)',
                    borderRadius: '50%',
                    borderTopColor: '#f39c12',
                    animation: 'spin 1s linear infinite',
                    marginBottom: '20px'
                  }}></div>
                  <p style={{ fontSize: '1.1rem', color: '#666' }}>{t('reports.loadingPendingChecks')}</p>
                </div>
              ) : pendingChecksError ? (
                <div style={{
                  color: '#ff416c',
                  marginBottom: '20px',
                  textAlign: 'center',
                  background: 'rgba(255, 65, 108, 0.1)',
                  padding: '15px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 65, 108, 0.3)',
                  fontWeight: '500'
                }}>
                  {pendingChecksError}
                </div>
              ) : pendingChecks.length === 0 ? (
                <div className="report-empty-state">
                  <span>{t('reports.noPendingChecksData')}</span>
                </div>
              ) : (
                <div className="pending-checks-list">
                  <div style={{ overflowX: 'auto' }}>
                    <table className="paper-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{
                          background: 'linear-gradient(135deg, rgba(243, 156, 18, 0.1) 0%, rgba(230, 126, 34, 0.1) 100%)'
                        }}>
                          <th style={{ padding: '15px', textAlign: 'start', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '20%', color: '#14253a' }}>
                            {t('reports.checkType')}
                          </th>
                          <th style={{ padding: '15px', textAlign: 'start', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '15%', color: '#14253a' }}>
                            {t('reports.status')}
                          </th>
                          <th style={{ padding: '15px', textAlign: 'start', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '15%', color: '#14253a' }}>
                            {t('reports.createdAt')}
                          </th>
                          <th style={{ padding: '15px', textAlign: 'start', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '20%', color: '#14253a' }}>
                            {t('reports.description')}
                          </th>
                          <th style={{ padding: '15px', textAlign: 'start', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '30%', color: '#14253a' }}>
                            {t('reports.actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingChecks.map((pendingCheck, index) => (
                          <tr key={pendingCheck.id || index} style={{
                            transition: 'background-color 0.3s ease',
                            '&:hover': { backgroundColor: 'rgba(243, 156, 18, 0.05)' }
                          }}>
                            <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                              <span style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                background: 'linear-gradient(135deg, rgba(243, 156, 18, 0.1) 0%, rgba(230, 126, 34, 0.1) 100%)',
                                color: '#e67e22',
                                border: '1px solid rgba(243, 156, 18, 0.3)'
                              }}>
                                {getCheckTypeName(pendingCheck.check_type)}
                              </span>
                            </td>
                            <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: '600',
                                ...(pendingCheck.status === 'pending' ? {
                                  background: 'rgba(241, 196, 15, 0.1)',
                                  color: '#f39c12',
                                  border: '1px solid rgba(241, 196, 15, 0.3)'
                                } : pendingCheck.status === 'approved' ? {
                                  background: 'rgba(46, 204, 113, 0.1)',
                                  color: '#27ae60',
                                  border: '1px solid rgba(46, 204, 113, 0.3)'
                                } : pendingCheck.status === 'completed' ? {
                                  background: 'rgba(52, 152, 219, 0.1)',
                                  color: '#3498db',
                                  border: '1px solid rgba(52, 152, 219, 0.3)'
                                } : {
                                  background: 'rgba(231, 76, 60, 0.1)',
                                  color: '#e74c3c',
                                  border: '1px solid rgba(231, 76, 60, 0.3)'
                                })
                              }}>
                                {t(`reports.pendingCheckStatus.${pendingCheck.status}`)}
                              </span>
                            </td>
                            <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                              {formatDate(pendingCheck.created_at)}
                            </td>
                            <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                              {pendingCheck.description || t('reports.noDescription')}
                            </td>
                            <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                              {pendingCheck.status === 'pending' ? (
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                  {isUserCreatedCheck(pendingCheck) ? (
                                    // 用户创建的待检查项：显示"确定"和"删除"按钮
                                    <>
                                      <button
                                        onClick={() => handleReviewPendingCheck(pendingCheck.id, 'approved')}
                                        disabled={reviewSubmitting}
                                        style={{
                                          ...getButtonStyle('success'),
                                          padding: '6px 12px',
                                          fontSize: '12px',
                                          minWidth: '60px'
                                        }}
                                        onMouseEnter={(e) => handleButtonHover(e, true)}
                                        onMouseLeave={(e) => handleButtonHover(e, false)}
                                      >
                                        {t('reports.confirmUserCreated')}
                                      </button>
                                      <button
                                        onClick={() => handleDeletePendingCheck(pendingCheck.id)}
                                        disabled={reviewSubmitting}
                                        style={{
                                          ...getButtonStyle('danger'),
                                          padding: '6px 12px',
                                          fontSize: '12px',
                                          minWidth: '60px'
                                        }}
                                        onMouseEnter={(e) => handleButtonHover(e, true)}
                                        onMouseLeave={(e) => handleButtonHover(e, false)}
                                      >
                                        {t('reports.deleteCheck')}
                                      </button>
                                    </>
                                  ) : (
                                    // 自动创建的待检查项：显示"通过"和"拒绝"按钮
                                    <>
                                      <button
                                        onClick={() => handleReviewPendingCheck(pendingCheck.id, 'approved')}
                                        disabled={reviewSubmitting}
                                        style={{
                                          ...getButtonStyle('success'),
                                          padding: '6px 12px',
                                          fontSize: '12px',
                                          minWidth: '60px'
                                        }}
                                        onMouseEnter={(e) => handleButtonHover(e, true)}
                                        onMouseLeave={(e) => handleButtonHover(e, false)}
                                      >
                                        {t('reports.approve')}
                                      </button>
                                      <button
                                        onClick={() => handleReviewPendingCheck(pendingCheck.id, 'rejected')}
                                        disabled={reviewSubmitting}
                                        style={{
                                          ...getButtonStyle('danger'),
                                          padding: '6px 12px',
                                          fontSize: '12px',
                                          minWidth: '60px'
                                        }}
                                        onMouseEnter={(e) => handleButtonHover(e, true)}
                                        onMouseLeave={(e) => handleButtonHover(e, false)}
                                      >
                                        {t('reports.reject')}
                                      </button>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <div style={{ color: '#556476', fontSize: '14px' }}>
                                  {isUserCreatedCheck(pendingCheck) ? (
                                    // 用户创建的待检查项审核后的显示
                                    <>
                                      {pendingCheck.reviewer_name && (
                                        <div>{t('reports.confirmedBy')}: {pendingCheck.reviewer_name}</div>
                                      )}
                                      {pendingCheck.reviewed_at && (
                                        <div style={{ fontSize: '12px', marginTop: '4px', color: '#627487' }}>
                                          {formatDate(pendingCheck.reviewed_at)}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    // 自动创建的待检查项审核后的显示
                                    <>
                                      {pendingCheck.reviewer_name && (
                                        <div>{t('reports.reviewedBy')}: {pendingCheck.reviewer_name}</div>
                                      )}
                                      {pendingCheck.reviewed_at && (
                                        <div style={{ fontSize: '12px', marginTop: '4px', color: '#627487' }}>
                                          {formatDate(pendingCheck.reviewed_at)}
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 历史对话区域 */}
        <div className="report-detail-page__section" style={{
          marginBottom: '20px',
          background: 'rgba(250, 245, 236, 0.94)',
          borderRadius: '16px',
          padding: '30px',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.16)',
          backdropFilter: 'blur(18px)'
        }}>
          <ReportConversationHistory
            messages={conversations}
            loading={conversationsLoading}
            error={conversationsError}
            show={showConversations}
            onToggle={() => setShowConversations(!showConversations)}
            visitType={visit?.visit_type || report?.visit_type}
            conversationLanguage={visit?.language || report?.language}
            toggleButtonStyle={{
              ...getButtonStyle('secondary'),
              padding: '8px 16px',
              fontSize: '14px',
              minWidth: 'auto'
            }}
            onToggleButtonMouseEnter={(e) => handleButtonHover(e, true)}
            onToggleButtonMouseLeave={(e) => handleButtonHover(e, false)}
          />
        </div>

        {/* 审核表单 */}
        <div className="report-detail-page__section" style={{
          background: 'rgba(250, 245, 236, 0.94)',
          borderRadius: '16px',
          padding: '30px',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.16)',
          backdropFilter: 'blur(18px)'
        }}>
          <h3 style={{
            margin: '0 0 20px 0',
            fontSize: '1.5rem',
            fontWeight: '600',
            background: 'linear-gradient(135deg, #cfac67 0%, #43d6c5 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            {t('reports.reviewForm')}
          </h3>

          {report?.report_type === 'final' && report?.status === 'pending' && (
            <div className="medical-order-editor-stack">
              <MedicalOrderDraftEditor
                title={t('reports.examinationOrdersSectionTitle')}
                description={t('reports.examinationOrdersFooter')}
                addLabel={t('reports.addExaminationOrder')}
                removeLabel={t('reports.remove')}
                selectLabel={t('reports.selectCheckType')}
                descriptionPlaceholder={t('reports.examinationOrdersDescriptionPlaceholder')}
                drafts={examinationDrafts}
                onAdd={addExaminationDraft}
                onChange={updateExaminationDraft}
                onRemove={removeExaminationDraft}
                t={t}
                disabled={submitting}
                keyPrefix="examination-draft"
              />
              <PrescriptionDraftEditor
                ref={prescriptionEditorRef}
                title={t('reports.prescriptionSectionTitle')}
                description={t('reports.prescriptionEditorDescription')}
                drafts={prescriptionDrafts}
                onAdd={addPrescriptionDraft}
                onChange={updatePrescriptionDraft}
                onRemove={removePrescriptionDraft}
                invalidRows={invalidPrescriptionRows}
                disabled={submitting}
                t={t}
              />
              <MedicalOrderDraftEditor
                title={t('reports.appointmentDrafts')}
                description={t('reports.appointmentDraftsFooter')}
                addLabel={t('reports.addAppointmentOrder')}
                removeLabel={t('reports.remove')}
                selectLabel={t('reports.selectCheckType')}
                descriptionPlaceholder={t('reports.appointmentOrdersDescriptionPlaceholder')}
                drafts={appointmentDrafts}
                onAdd={addAppointmentDraft}
                onChange={updateAppointmentDraft}
                onRemove={removeAppointmentDraft}
                t={t}
                disabled={submitting}
                keyPrefix="appointment-draft"
              />
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="report-review-note" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: shouldBlockReviewForPendingChecks() ? '#999' : '#333'
              }}>
                {t('reports.reviewNote')}
                <span style={{ color: '#b42318', marginInlineStart: '6px', fontSize: '13px', fontWeight: '600' }}>
                  * {t('reports.reviewNoteRequired')}
                </span>
              </label>
              
              {shouldBlockReviewForPendingChecks() ? (
                <div style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '12px',
                  border: '2px solid rgba(241, 196, 15, 0.3)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'rgba(241, 196, 15, 0.1)',
                  color: '#856404',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxSizing: 'border-box',
                  textAlign: 'center',
                  flexDirection: 'column',
                  gap: '10px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                      <path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                      <path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                    </svg>
                    <strong>{t('reports.pendingChecksWarningTitle')}</strong>
                  </div>
                  <div>
                    {t('reports.pendingChecksWarningMessage')}
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.8 }}>
                    {t('reports.pendingChecksCount', { count: pendingChecks.filter(check => check.status === 'pending').length })}
                  </div>
                </div>
              ) : (
                <textarea
                  id="report-review-note"
                  className="report-review-textarea"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  disabled={!isReportReviewable}
                  required
                  aria-required="true"
                  aria-describedby="report-review-note-requirement"
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '12px',
                    border: '2px solid rgba(102, 126, 234, 0.2)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'rgba(255, 248, 241, 0.88)',
                    color: '#1a202c',
                    transition: 'all 0.3s ease',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                    opacity: !isReportReviewable ? 0.65 : 1,
                    cursor: !isReportReviewable ? 'not-allowed' : 'text'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(102, 126, 234, 0.5)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(102, 126, 234, 0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                  placeholder={t('reports.reviewNotePlaceholder')}
                />
              )}
              {!shouldBlockReviewForPendingChecks() && (
                <div
                  id="report-review-note-requirement"
                  style={{
                    marginTop: '7px',
                    color: hasRequiredReviewNote ? '#667085' : '#b42318',
                    fontSize: '13px',
                    lineHeight: 1.4
                  }}
                >
                  {t('reports.reviewNoteRequiredHint')}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                type="submit"
                disabled={submitting || !isReportReviewable || shouldBlockReviewForPendingChecks() || !hasRequiredReviewNote}
                style={{
                  ...getButtonStyle('success'),
                  opacity: (submitting || !isReportReviewable || shouldBlockReviewForPendingChecks() || !hasRequiredReviewNote) ? 0.4 : 1,
                  cursor: (submitting || !isReportReviewable || shouldBlockReviewForPendingChecks() || !hasRequiredReviewNote) ? 'not-allowed' : 'pointer',
                  filter: shouldBlockReviewForPendingChecks() ? 'grayscale(50%)' : 'none'
                }}
                onMouseEnter={(e) => !(submitting || !isReportReviewable || shouldBlockReviewForPendingChecks() || !hasRequiredReviewNote) && handleButtonHover(e, true)}
                onMouseLeave={(e) => !(submitting || !isReportReviewable || shouldBlockReviewForPendingChecks() || !hasRequiredReviewNote) && handleButtonHover(e, false)}
                title={!isReportReviewable
                  ? t('reports.reportNotReadyForReview')
                  : shouldBlockReviewForPendingChecks()
                    ? t('reports.reviewDisabledTooltip')
                    : !hasRequiredReviewNote
                      ? t('reports.reviewNoteRequiredHint')
                      : ''}
              >
                {getApproveButtonText()}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* 渲染段落编辑模式 */}
      {editingSection !== null && renderSectionEditMode()}

      {/* 创建待检查项目对话框 */}
      {showCreatePendingCheck && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="report-detail-page__modal" style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '30px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #cfac67 0%, #43d6c5 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {t('reports.createPendingCheckTitle')}
              </h3>
              <button
                onClick={() => {
                  setShowCreatePendingCheck(false);
                  setCreatePendingCheckData({ check_type: '', description: '' });
                  setError('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '5px'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: '#333'
              }}>
                {t('reports.checkType')}:
              </label>
              <select
                value={createPendingCheckData.check_type}
                onChange={(e) => setCreatePendingCheckData({
                  ...createPendingCheckData,
                  check_type: e.target.value
                })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid rgba(102, 126, 234, 0.2)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'rgba(255, 248, 241, 0.88)',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'rgba(102, 126, 234, 0.5)';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(102, 126, 234, 0.2)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="">{t('reports.selectCheckType')}</option>
                <option value="lab">{t('checkTypes.lab')}</option>
                <option value="quick">{t('checkTypes.quick')}</option>
                <option value="body">{t('checkTypes.body')}</option>
                <option value="scan">{t('checkTypes.scan')}</option>
                <option value="tissue">{t('checkTypes.tissue')}</option>
                <option value="scope">{t('checkTypes.scope')}</option>
                <option value="other">{t('checkTypes.other')}</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: '#333'
              }}>
                {t('reports.description')}:
              </label>
              <textarea
                value={createPendingCheckData.description}
                onChange={(e) => setCreatePendingCheckData({
                  ...createPendingCheckData,
                  description: e.target.value
                })}
                placeholder={t('reports.createPendingCheckDescriptionPlaceholder')}
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '12px',
                  border: '2px solid rgba(102, 126, 234, 0.2)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'rgba(255, 248, 241, 0.88)',
                  color: '#1a202c',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box',
                  resize: 'vertical'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'rgba(102, 126, 234, 0.5)';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(102, 126, 234, 0.2)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '15px',
              marginTop: '30px'
            }}>
              <button
                onClick={() => {
                  setShowCreatePendingCheck(false);
                  setCreatePendingCheckData({ check_type: '', description: '' });
                  setError('');
                }}
                disabled={creatingPendingCheck}
                style={{
                  ...getButtonStyle('dark'),
                  padding: '10px 20px',
                  fontSize: '14px',
                  opacity: creatingPendingCheck ? 0.4 : 1,
                  cursor: creatingPendingCheck ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => !creatingPendingCheck && handleButtonHover(e, true)}
                onMouseLeave={(e) => !creatingPendingCheck && handleButtonHover(e, false)}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreatePendingCheck}
                disabled={creatingPendingCheck || !createPendingCheckData.check_type || !createPendingCheckData.description}
                style={{
                  ...getButtonStyle('success'),
                  padding: '10px 20px',
                  fontSize: '14px',
                  opacity: (creatingPendingCheck || !createPendingCheckData.check_type || !createPendingCheckData.description) ? 0.4 : 1,
                  cursor: (creatingPendingCheck || !createPendingCheckData.check_type || !createPendingCheckData.description) ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => !(creatingPendingCheck || !createPendingCheckData.check_type || !createPendingCheckData.description) && handleButtonHover(e, true)}
                onMouseLeave={(e) => !(creatingPendingCheck || !createPendingCheckData.check_type || !createPendingCheckData.description) && handleButtonHover(e, false)}
              >
                {creatingPendingCheck ? t('reports.creating') : t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ReportReview; 
